import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    FiBookOpen,
    FiSearch,
    FiEdit2,
    FiTrash2,
    FiCopy,
    FiCheck,
    FiDownload,
    FiX,
    FiTag,
    FiMaximize2,
    FiChevronLeft,
    FiChevronRight,
    FiChevronUp,
    FiChevronDown,
    FiArrowRight,
    FiImage,
    FiUpload,
    FiFolder,
    FiCompass,
    FiCrosshair
} from "react-icons/fi";
import {
    DEFAULT_DICTIONARY_CATEGORIES,
    DEFAULT_DICTIONARY_TERMS,
    loadMarkdownDictionary
} from "../../constants/marketDictionaryData";
import {
    Overlay,
    Container,
    Header,
    HeaderTitleBlock,
    IconBadge,
    Title,
    NavArrowsGroup,
    NavArrowBtn,
    HomeContainer,
    HomeHero,
    HomeHeroTitle,
    DocsCountPill,
    HomeCategoriesList,
    HomeCategorySection,
    HomeCategoryHeader,
    HomeCategoryHeaderLeft,
    HomeCategoryIconWrapper,
    HomeCategoryTitle,
    HomeCategoryPreviewText,
    HomeCategoryHeaderRight,
    HomeCategoryChevron,
    HomeCategoryCardsWrapper,
    HomeExpandAllBtn,
    HomeHeroActions,
    DownloadAllPdfBtn,
    HomeBlueprintBtn,
    BlueprintTopRow,
    BlueprintCopyBtn,
    BlueprintContainer,
    BlueprintSectionTitle,
    BlueprintPillarsGrid,
    BlueprintPillarCard,
    PillarHeader,
    PillarTag,
    PillarTitle,
    PillarBody,
    PillarHighlight,
    BlueprintStagesWrapper,
    StageCard,
    StageHeader,
    StageNumber,
    StageTitleWrapper,
    StageTitle,
    StageTagline,
    StageRoleDesc,
    StageDocChips,
    BlueprintDocChip,
    WorkflowContainer,
    WorkflowHeader,
    WorkflowStepsGrid,
    WorkflowStepBox,
    WorkflowStepNumber,
    WorkflowStepText,
    WorkflowStepSub,
    DocCardsGrid,
    DocCard,
    DocCardHeader,
    DocCardTitle,
    DocCardDesc,
    DocCardFooter,
    ViewDocPrompt,
    HeaderActions,
    ActionBtn,
    CloseBtn,
    Toolbar,
    SearchBox,
    SearchInput,
    SearchIconWrapper,
    ClearSearchBtn,
    SuggestionsDropdown,
    SuggestionsHeader,
    SuggestionItem,
    SuggestionTitleRow,
    SuggestionTitle,
    SuggestionSnippet,
    SnippetBadge,
    BodyLayout,
    CollapseToggleBtn,
    Sidebar,
    TermsList,
    ScrollNavBtn,
    CategoryGroup,
    CategoryHeader,
    CategoryHeaderLeft,
    CategoryChevron,
    CategoryTitle,
    CategoryCountBadge,
    CategoryTermsContainer,
    TermItem,
    TermBullet,
    TermItemTitle,
    DetailPanel,
    DetailHeader,
    DetailTitle,
    DetailMetaRow,
    CategoryBadge,
    CustomBadge,
    ShortDefCallout,
    SectionBlock,
    SectionHeading,
    DiagramContainer,
    CopyDiagramBtn,
    DocSectionCard,
    DocSectionHeader,
    DocSectionBadge,
    DocSectionTitle,
    TagsRow,
    TagChip,
    InlineEditForm,
    FormGroup,
    Label,
    Input,
    Select,
    Textarea,
    TextareaMonospace,
    ImageWrapper,
    ImageCaptionBar,
    LightboxOverlay,
    LightboxImg,
    LightboxCloseBtn
} from "./technical-dictionary/dictionaryStyles";
import { PDF_DOCUMENT_STYLES } from "./technical-dictionary/dictionaryPrintStyles";
import {
    DictionaryNavContext,
    getMatchingSnippet,
    HighlightText,
    resolveTagToTermId,
    renderInlineMarkdown,
    findMultiTfElement,
    highlightTargetElement,
    getHistoryItemId,
    getHistoryItemScroll
} from "./technical-dictionary/dictionaryTagHelpers";
import {
    DocRichViewer,
    formatTitleWithSubtitle
} from "./technical-dictionary/DocRichViewer";

const STORAGE_KEY = "marketVisionTechnicalDictionary_v253_en";

export const TechnicalDictionaryModal = ({ isOpen, onClose }) => {
    // Dictionary state loaded from localStorage or default constants
    const [terms, setTerms] = useState(() => {
        // Clean up obsolete dictionary keys from old versions to immediately free localStorage quota
        try {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith("marketVisionTechnicalDictionary_") && key !== STORAGE_KEY) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(k => localStorage.removeItem(k));
        } catch (e) {
            console.warn("Storage cleanup error:", e);
        }

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    const defIds = new Set(DEFAULT_DICTIONARY_TERMS.map(d => d.id));
                    const customOnly = parsed.filter(t => t.isCustom && !defIds.has(t.id) && t.id !== "price_analysis_descriptive_vs_causal");
                    return [...DEFAULT_DICTIONARY_TERMS, ...customOnly];
                }
            } catch (e) {
                console.error("Failed to parse saved dictionary", e);
            }
        }
        return DEFAULT_DICTIONARY_TERMS;
    });

    const [searchQuery, setSearchQuery] = useState("");
    const [viewMode, setViewMode] = useState("home"); // "home" | "detail"
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [selectedTermId, setSelectedTermId] = useState(DEFAULT_DICTIONARY_TERMS[0]?.id || "");
    const [navHistory, setNavHistory] = useState(() => {
        const initialId = DEFAULT_DICTIONARY_TERMS[0]?.id || "timeframes";
        return {
            stack: [{ id: initialId, scrollTop: 0 }],
            index: 0
        };
    });
    const [copiedId, setCopiedId] = useState(null);
    const [isExportingAllPdf, setIsExportingAllPdf] = useState(false);
    const [showRoadmapGuide, setShowRoadmapGuide] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingTerm, setEditingTerm] = useState(null);
    const [lightboxImage, setLightboxImage] = useState(null);
    const [activeIllustrationIndex, setActiveIllustrationIndex] = useState(0);

    // Reset illustration index on term change
    useEffect(() => {
        setActiveIllustrationIndex(0);
    }, [selectedTermId]);

    const searchContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const termsListRef = useRef(null);
    const detailPanelRef = useRef(null);
    const homeContainerRef = useRef(null);
    const pendingScrollSectionRef = useRef(null);
    const docScrollPositionsRef = useRef({});
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(true);

    const handleScrollUp = () => {
        if (termsListRef.current) {
            termsListRef.current.scrollBy({ top: -160, behavior: "smooth" });
            setTimeout(() => {
                if (termsListRef.current) {
                    const { scrollTop, scrollHeight, clientHeight } = termsListRef.current;
                    setCanScrollUp(scrollTop > 4);
                    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 4);
                }
            }, 250);
        }
    };

    const handleScrollDown = () => {
        if (termsListRef.current) {
            termsListRef.current.scrollBy({ top: 160, behavior: "smooth" });
            setTimeout(() => {
                if (termsListRef.current) {
                    const { scrollTop, scrollHeight, clientHeight } = termsListRef.current;
                    setCanScrollUp(scrollTop > 4);
                    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 4);
                }
            }, 250);
        }
    };

    // Save only custom terms to localStorage to strictly prevent QuotaExceededError
    useEffect(() => {
        try {
            const defIds = new Set(DEFAULT_DICTIONARY_TERMS.map(d => d.id));
            const customTerms = terms.filter(t => t.isCustom && !defIds.has(t.id));
            if (customTerms.length > 0) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(customTerms));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch (err) {
            console.warn("Could not write dictionary to localStorage (quota protected):", err);
            try {
                // Emergency cleanup of any other old keys
                for (let i = localStorage.length - 1; i >= 0; i--) {
                    const k = localStorage.key(i);
                    if (k && k.startsWith("marketVisionTechnicalDictionary_") && k !== STORAGE_KEY) {
                        localStorage.removeItem(k);
                    }
                }
            } catch (cleanupErr) {
                // ignore
            }
        }
    }, [terms]);

    // Live sync: fetch and parse fresh Markdown documentation files while preserving user edits
    useEffect(() => {
        let isMounted = true;
        loadMarkdownDictionary().then(liveDocs => {
            if (isMounted && liveDocs && liveDocs.length > 0) {
                setTerms(prev => {
                    const customMap = new Map();
                    prev.forEach(t => {
                        if (t.isCustom) {
                            customMap.set(t.id, t);
                        }
                    });

                    const merged = liveDocs.map(doc => {
                        if (customMap.has(doc.id)) {
                            return customMap.get(doc.id);
                        }
                        return doc;
                    });

                    const liveIds = new Set(liveDocs.map(d => d.id));
                    const extraCustom = prev.filter(t => t.isCustom && !liveIds.has(t.id) && t.id !== "price_analysis_descriptive_vs_causal");

                    return [...merged, ...extraCustom];
                });
            }
        });
        return () => { isMounted = false; };
    }, [isOpen]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
                setIsSearchDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Full-text search with multi-keyword token matching, context snippet extraction, and relevance scoring
    const filteredTerms = useMemo(() => {
        if (!searchQuery.trim()) return terms;

        const tokens = searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);

        return terms
            .map(item => {
                const titleText = (item.term || "").toLowerCase();
                const shortText = (item.shortDef || "").toLowerCase();
                const fullText = (item.fullDescription || "").toLowerCase();
                const ruleText = (item.ruleOrFormula || "").toLowerCase();
                const appText = (item.application || "").toLowerCase();
                const diagramText = (item.diagram || "").toLowerCase();
                const catText = (item.category || "").toLowerCase();
                const tagsText = (item.tags || []).join(" ").toLowerCase();

                const combinedAll = `${titleText} ${shortText} ${fullText} ${ruleText} ${appText} ${diagramText} ${catText} ${tagsText}`;

                // Calculate match count & token presence
                let matchedTokensCount = 0;
                tokens.forEach(token => {
                    if (combinedAll.includes(token)) {
                        matchedTokensCount++;
                    }
                });

                if (matchedTokensCount === 0) return null;

                // Calculate relevance score
                let score = 0;
                // Significant boost if document matches all query tokens
                if (matchedTokensCount === tokens.length) {
                    score += 200;
                } else {
                    score += matchedTokensCount * 30;
                }

                tokens.forEach(token => {
                    if (titleText.includes(token)) score += 120;
                    if (ruleText.includes(token)) score += 50;
                    if (tagsText.includes(token)) score += 40;
                    if (shortText.includes(token)) score += 30;
                    if (appText.includes(token)) score += 25;
                    if (fullText.includes(token)) score += 20;
                    if (diagramText.includes(token)) score += 10;
                });

                const matchSnippet = getMatchingSnippet(item, searchQuery);

                return {
                    ...item,
                    _score: score,
                    _snippet: matchSnippet
                };
            })
            .filter(Boolean)
            .sort((a, b) => b._score - a._score);
    }, [terms, searchQuery]);

    // Ensure selected term exists in current filter or fallback
    useEffect(() => {
        if (filteredTerms.length > 0) {
            const exists = filteredTerms.some(t => t.id === selectedTermId);
            if (!exists) {
                setSelectedTermId(filteredTerms[0].id);
            }
        }
    }, [filteredTerms, selectedTermId]);

    // State to track expanded categories in the sidebar accordion
    const [expandedCategories, setExpandedCategories] = useState(() => {
        const initial = {};
        DEFAULT_DICTIONARY_CATEGORIES.forEach(c => {
            initial[c] = false;
        });
        if (DEFAULT_DICTIONARY_CATEGORIES[0]) {
            initial[DEFAULT_DICTIONARY_CATEGORIES[0]] = true;
        }
        return initial;
    });

    // Auto-expand category of active/selected term
    useEffect(() => {
        if (!selectedTermId) return;
        const currentTerm = terms.find(t => t.id === selectedTermId);
        if (currentTerm && currentTerm.category) {
            setExpandedCategories(prev => {
                if (prev[currentTerm.category]) return prev;
                return {
                    ...prev,
                    [currentTerm.category]: true
                };
            });
        }
    }, [selectedTermId, terms]);

    // Group filtered terms by category preserving default category order
    const groupedCategories = useMemo(() => {
        const categoryMap = new Map();
        DEFAULT_DICTIONARY_CATEGORIES.forEach(cat => {
            categoryMap.set(cat, []);
        });

        filteredTerms.forEach(term => {
            const cat = term.category || "Market Context";
            if (!categoryMap.has(cat)) {
                categoryMap.set(cat, []);
            }
            categoryMap.get(cat).push(term);
        });

        const result = [];
        categoryMap.forEach((termsList, catName) => {
            if (termsList.length > 0) {
                result.push({
                    name: catName,
                    terms: termsList
                });
            }
        });
        return result;
    }, [filteredTerms]);

    const toggleCategory = (catName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [catName]: !prev[catName]
        }));
    };

    const isCategoryExpanded = (catName) => {
        if (searchQuery.trim().length > 0) return true;
        return !!expandedCategories[catName];
    };

    // State to track expanded categories on the Home page
    const [expandedHomeCategories, setExpandedHomeCategories] = useState({});

    const toggleHomeCategory = (catName) => {
        setExpandedHomeCategories(prev => ({
            ...prev,
            [catName]: !prev[catName]
        }));
    };

    const isHomeCategoryExpanded = (catName) => {
        if (searchQuery.trim().length > 0) return true;
        return !!expandedHomeCategories[catName];
    };

    const allHomeExpanded = useMemo(() => {
        if (groupedCategories.length === 0) return false;
        return groupedCategories.every(g => isHomeCategoryExpanded(g.name));
    }, [groupedCategories, expandedHomeCategories, searchQuery]);

    const toggleAllHomeCategories = () => {
        const nextState = !allHomeExpanded;
        const updated = {};
        groupedCategories.forEach(g => {
            updated[g.name] = nextState;
        });
        setExpandedHomeCategories(updated);
    };

    // Track scrollability to toggle top/bottom arrow buttons
    useEffect(() => {
        const checkScrollPosition = () => {
            if (!termsListRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = termsListRef.current;
            setCanScrollUp(scrollTop > 4);
            setCanScrollDown(scrollTop + clientHeight < scrollHeight - 4);
        };

        checkScrollPosition();
        const timer = setTimeout(checkScrollPosition, 60);

        const el = termsListRef.current;
        if (el) {
            el.addEventListener("scroll", checkScrollPosition, { passive: true });
            window.addEventListener("resize", checkScrollPosition);
            return () => {
                clearTimeout(timer);
                el.removeEventListener("scroll", checkScrollPosition);
                window.removeEventListener("resize", checkScrollPosition);
            };
        }
        return () => clearTimeout(timer);
    }, [filteredTerms, isSidebarOpen, viewMode, expandedCategories]);

    const activeTerm = useMemo(() => {
        return terms.find(t => t.id === selectedTermId) || filteredTerms[0] || null;
    }, [terms, selectedTermId, filteredTerms]);

    const activeImages = useMemo(() => {
        if (!activeTerm) return [];
        if (activeTerm.images && activeTerm.images.length > 0) {
            return activeTerm.images;
        }
        if (activeTerm.imageUrl) {
            return [{ url: activeTerm.imageUrl, caption: activeTerm.imageCaption || "" }];
        }
        return [];
    }, [activeTerm]);

    const currentIllustrationIndex = (activeIllustrationIndex >= 0 && activeIllustrationIndex < activeImages.length)
        ? activeIllustrationIndex
        : 0;

    const currentIllustration = activeImages[currentIllustrationIndex];

    const shouldInsertIllustrationBeforeKeyLevels = Boolean(
        activeImages.length > 0 && (
            activeTerm?.id === 'market_structure' ||
            activeTerm?.id === 'market_structure_logic' ||
            activeTerm?.id === 'external_vs_internal_structure' ||
            (activeTerm?.fullDescription && /primary structure and key price levels/i.test(activeTerm.fullDescription))
        )
    );

    const handlePrevImage = () => {
        if (activeImages.length <= 1) return;
        const prevIdx = (currentIllustrationIndex - 1 + activeImages.length) % activeImages.length;
        setActiveIllustrationIndex(prevIdx);
        if (lightboxImage) {
            setLightboxImage(activeImages[prevIdx].url);
        }
    };

    const handleNextImage = () => {
        if (activeImages.length <= 1) return;
        const nextIdx = (currentIllustrationIndex + 1) % activeImages.length;
        setActiveIllustrationIndex(nextIdx);
        if (lightboxImage) {
            setLightboxImage(activeImages[nextIdx].url);
        }
    };

    useEffect(() => {
        if (!lightboxImage) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setLightboxImage(null);
            } else if (activeImages.length > 1) {
                if (e.key === 'ArrowLeft') {
                    const prevIdx = (currentIllustrationIndex - 1 + activeImages.length) % activeImages.length;
                    setActiveIllustrationIndex(prevIdx);
                    setLightboxImage(activeImages[prevIdx].url);
                } else if (e.key === 'ArrowRight') {
                    const nextIdx = (currentIllustrationIndex + 1) % activeImages.length;
                    setActiveIllustrationIndex(nextIdx);
                    setLightboxImage(activeImages[nextIdx].url);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [lightboxImage, activeImages, currentIllustrationIndex]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchQuery(val);
        setHighlightedIndex(0);
        setIsSearchDropdownOpen(val.trim().length > 0);
    };

    const handleSearchKeyDown = (e) => {
        if (!isSearchDropdownOpen || filteredTerms.length === 0) {
            if (e.key === "ArrowDown" && filteredTerms.length > 0) {
                setIsSearchDropdownOpen(true);
                setHighlightedIndex(0);
                e.preventDefault();
            }
            return;
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex(prev => (prev + 1) % filteredTerms.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex(prev => (prev - 1 + filteredTerms.length) % filteredTerms.length);
        } else if (e.key === "Enter") {
            e.preventDefault();
            const targetDoc = filteredTerms[highlightedIndex] || filteredTerms[0];
            if (targetDoc) {
                navigateToDoc(targetDoc.id);
            }
            setIsSearchDropdownOpen(false);
        } else if (e.key === "Escape") {
            setIsSearchDropdownOpen(false);
        }
    };

    // Reset scroll to top (or Multi-TF section / restored scroll position if requested) whenever viewing a document or changing documents
    useEffect(() => {
        const panel = detailPanelRef.current;
        if (!panel) return;

        const pending = pendingScrollSectionRef.current;
        pendingScrollSectionRef.current = null;

        if (pending && (pending.target === 'multi-tf' || pending.target === 'tag-target')) {
            const tagText = pending.tagText;
            const docId = pending.docId || selectedTermId;
            let userInteracted = false;
            const onUserInteraction = () => {
                userInteracted = true;
            };
            panel.addEventListener('wheel', onUserInteraction, { passive: true });
            panel.addEventListener('touchmove', onUserInteraction, { passive: true });
            panel.addEventListener('keydown', onUserInteraction, { passive: true });

            const scrollToTarget = (behavior = 'smooth') => {
                if (userInteracted || !panel) return false;
                const targetEl = findMultiTfElement(panel, tagText, docId);
                if (targetEl) {
                    const panelRect = panel.getBoundingClientRect();
                    const targetRect = targetEl.getBoundingClientRect();
                    const targetOffsetTop = targetRect.top - panelRect.top + panel.scrollTop;
                    const desiredTop = Math.max(0, targetOffsetTop - 14);

                    // Re-align if difference is more than 8px
                    if (Math.abs(panel.scrollTop - desiredTop) > 8) {
                        panel.scrollTo({ top: desiredTop, behavior });
                    }
                    highlightTargetElement(targetEl);
                    return true;
                }
                return false;
            };

            // 1. Initial attempt immediately
            const foundInitial = scrollToTarget('smooth');
            if (!foundInitial) {
                // If this tag has no specific anchor in this document, stay at top of page
                panel.scrollTop = 0;
            }

            // 2. Observe any images that are currently loading and re-align on each load
            const imgs = panel.querySelectorAll('img');
            imgs.forEach(img => {
                if (!img.complete) {
                    img.addEventListener('load', () => {
                        scrollToTarget('smooth');
                    }, { once: true });
                }
            });

            // 3. Observe content resizing via ResizeObserver (covers SVG carousels, markdown layout)
            let resizeObserver = null;
            if (typeof ResizeObserver !== 'undefined') {
                resizeObserver = new ResizeObserver(() => {
                    if (!userInteracted) {
                        scrollToTarget('smooth');
                    }
                });
                resizeObserver.observe(panel);
                if (panel.firstElementChild) {
                    resizeObserver.observe(panel.firstElementChild);
                }
            }

            // 4. Staggered time checkpoints to guarantee exact arrival as layout settles
            const delays = [50, 120, 250, 450, 750, 1100, 1600];
            const timers = delays.map((delay, idx) =>
                setTimeout(() => {
                    const success = scrollToTarget('smooth');
                    if (!success && idx === delays.length - 1 && !foundInitial) {
                        panel.scrollTop = 0;
                    }
                }, delay)
            );

            // 5. Cleanup after 2.2 seconds
            const cleanupTimer = setTimeout(() => {
                if (resizeObserver) resizeObserver.disconnect();
                panel.removeEventListener('wheel', onUserInteraction);
                panel.removeEventListener('touchmove', onUserInteraction);
                panel.removeEventListener('keydown', onUserInteraction);
            }, 2200);

            return () => {
                if (resizeObserver) resizeObserver.disconnect();
                timers.forEach(t => clearTimeout(t));
                clearTimeout(cleanupTimer);
                panel.removeEventListener('wheel', onUserInteraction);
                panel.removeEventListener('touchmove', onUserInteraction);
                panel.removeEventListener('keydown', onUserInteraction);
            };
        } else if (pending && pending.target === 'restore-scroll') {
            const desiredScroll = pending.scrollTop || 0;
            let cancelledByManualScroll = false;
            const onUserWheelOrTouch = () => {
                cancelledByManualScroll = true;
            };
            panel.addEventListener('wheel', onUserWheelOrTouch, { passive: true, once: true });
            panel.addEventListener('touchmove', onUserWheelOrTouch, { passive: true, once: true });

            const applyScroll = () => {
                if (panel && !cancelledByManualScroll) {
                    panel.scrollTop = desiredScroll;
                }
            };

            applyScroll();
            const frameId = requestAnimationFrame(() => {
                applyScroll();
                const timer1 = setTimeout(applyScroll, 40);
                const timer2 = setTimeout(applyScroll, 120);
                return () => {
                    clearTimeout(timer1);
                    clearTimeout(timer2);
                    panel.removeEventListener('wheel', onUserWheelOrTouch);
                    panel.removeEventListener('touchmove', onUserWheelOrTouch);
                };
            });
            return () => {
                cancelAnimationFrame(frameId);
                panel.removeEventListener('wheel', onUserWheelOrTouch);
                panel.removeEventListener('touchmove', onUserWheelOrTouch);
            };
        } else {
            panel.scrollTop = 0;
        }
    }, [selectedTermId, viewMode]);

    const navigateToDoc = useCallback((termId, options = {}) => {
        if (!termId) return;
        const { targetSection = null, tagText = '' } = options;

        setIsEditing(false);
        setEditingTerm(null);
        setIsSearchDropdownOpen(false);
        setViewMode("detail");

        if (targetSection) {
            pendingScrollSectionRef.current = { target: targetSection, tagText, docId: termId };
        } else {
            pendingScrollSectionRef.current = null;
        }

        // If already viewing this document, scroll immediately
        if (selectedTermId === termId) {
            const panel = detailPanelRef.current;
            if (panel) {
                if (targetSection === 'tag-target' || targetSection === 'multi-tf') {
                    const doScroll = () => {
                        const targetEl = findMultiTfElement(panel, tagText, termId);
                        if (targetEl) {
                            const panelRect = panel.getBoundingClientRect();
                            const targetRect = targetEl.getBoundingClientRect();
                            const targetOffsetTop = targetRect.top - panelRect.top + panel.scrollTop;
                            panel.scrollTo({ top: Math.max(0, targetOffsetTop - 14), behavior: 'smooth' });
                            highlightTargetElement(targetEl);
                            return true;
                        }
                        return false;
                    };
                    if (!doScroll()) {
                        panel.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                        setTimeout(doScroll, 100);
                        setTimeout(doScroll, 300);
                    }
                } else {
                    panel.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
            return;
        }

        const currentScroll = detailPanelRef.current ? detailPanelRef.current.scrollTop : (docScrollPositionsRef.current[selectedTermId] || 0);
        if (selectedTermId) {
            docScrollPositionsRef.current[selectedTermId] = currentScroll;
        }

        setSelectedTermId(termId);

        setNavHistory(prev => {
            const currentItem = prev.stack[prev.index];
            const currentId = getHistoryItemId(currentItem);

            if (currentId === termId) {
                return prev;
            }

            const newStack = prev.stack.slice(0, prev.index + 1);
            if (newStack[prev.index]) {
                newStack[prev.index] = {
                    id: currentId,
                    scrollTop: currentScroll
                };
            }
            newStack.push({
                id: termId,
                scrollTop: 0
            });

            return {
                stack: newStack,
                index: newStack.length - 1
            };
        });
    }, [selectedTermId]);

    const navigateToTag = useCallback((tagText, targetDocId) => {
        const resolvedId = targetDocId || resolveTagToTermId(tagText, terms);
        if (resolvedId) {
            navigateToDoc(resolvedId, { targetSection: 'tag-target', tagText });
        }
    }, [terms, navigateToDoc]);

    const handleSelectProposal = (termId) => {
        navigateToDoc(termId);
    };

    const handleSelectDocFromHome = (termId) => {
        navigateToDoc(termId);
    };

    const handleGoBack = () => {
        setIsEditing(false);
        setEditingTerm(null);

        const currentScroll = detailPanelRef.current ? detailPanelRef.current.scrollTop : (docScrollPositionsRef.current[selectedTermId] || 0);
        if (selectedTermId) {
            docScrollPositionsRef.current[selectedTermId] = currentScroll;
        }

        if (viewMode === "home") {
            setViewMode("detail");
            const currentItem = navHistory.stack[navHistory.index];
            if (currentItem) {
                const targetId = getHistoryItemId(currentItem);
                const targetScroll = getHistoryItemScroll(currentItem) ?? docScrollPositionsRef.current[targetId] ?? 0;
                pendingScrollSectionRef.current = { target: 'restore-scroll', scrollTop: targetScroll };
                if (selectedTermId === targetId) {
                    if (detailPanelRef.current) {
                        detailPanelRef.current.scrollTop = targetScroll;
                    }
                } else {
                    setSelectedTermId(targetId);
                }
            }
            return;
        }

        if (navHistory.index > 0) {
            const newIndex = navHistory.index - 1;
            const targetItem = navHistory.stack[newIndex];
            const targetId = getHistoryItemId(targetItem);
            const targetScroll = getHistoryItemScroll(targetItem) ?? docScrollPositionsRef.current[targetId] ?? 0;

            // Save the current document's scroll before leaving it
            setNavHistory(prev => {
                const newStack = [...prev.stack];
                if (newStack[prev.index]) {
                    newStack[prev.index] = {
                        id: getHistoryItemId(newStack[prev.index]),
                        scrollTop: currentScroll
                    };
                }
                return {
                    stack: newStack,
                    index: newIndex
                };
            });

            pendingScrollSectionRef.current = { target: 'restore-scroll', scrollTop: targetScroll };
            if (selectedTermId === targetId) {
                if (detailPanelRef.current) {
                    detailPanelRef.current.scrollTop = targetScroll;
                }
            } else {
                setSelectedTermId(targetId);
            }
            setViewMode("detail");
        }
    };

    const handleGoForward = () => {
        setIsEditing(false);
        setEditingTerm(null);

        const currentScroll = detailPanelRef.current ? detailPanelRef.current.scrollTop : (docScrollPositionsRef.current[selectedTermId] || 0);
        if (selectedTermId) {
            docScrollPositionsRef.current[selectedTermId] = currentScroll;
        }

        if (viewMode === "home") {
            setViewMode("detail");
            const currentItem = navHistory.stack[navHistory.index];
            if (currentItem) {
                const targetId = getHistoryItemId(currentItem);
                const targetScroll = getHistoryItemScroll(currentItem) ?? docScrollPositionsRef.current[targetId] ?? 0;
                pendingScrollSectionRef.current = { target: 'restore-scroll', scrollTop: targetScroll };
                if (selectedTermId === targetId) {
                    if (detailPanelRef.current) {
                        detailPanelRef.current.scrollTop = targetScroll;
                    }
                } else {
                    setSelectedTermId(targetId);
                }
            }
            return;
        }

        if (navHistory.index < navHistory.stack.length - 1) {
            const newIndex = navHistory.index + 1;
            const targetItem = navHistory.stack[newIndex];
            const targetId = getHistoryItemId(targetItem);
            const targetScroll = getHistoryItemScroll(targetItem) ?? docScrollPositionsRef.current[targetId] ?? 0;

            // Save the current document's scroll before leaving it
            setNavHistory(prev => {
                const newStack = [...prev.stack];
                if (newStack[prev.index]) {
                    newStack[prev.index] = {
                        id: getHistoryItemId(newStack[prev.index]),
                        scrollTop: currentScroll
                    };
                }
                return {
                    stack: newStack,
                    index: newIndex
                };
            });

            pendingScrollSectionRef.current = { target: 'restore-scroll', scrollTop: targetScroll };
            if (selectedTermId === targetId) {
                if (detailPanelRef.current) {
                    detailPanelRef.current.scrollTop = targetScroll;
                }
            } else {
                setSelectedTermId(targetId);
            }
            setViewMode("detail");
        }
    };

    const handleGoHome = () => {
        setIsEditing(false);
        setEditingTerm(null);

        const currentScroll = detailPanelRef.current ? detailPanelRef.current.scrollTop : (docScrollPositionsRef.current[selectedTermId] || 0);
        if (selectedTermId) {
            docScrollPositionsRef.current[selectedTermId] = currentScroll;
        }

        if (navHistory.stack[navHistory.index]) {
            setNavHistory(prev => {
                const newStack = [...prev.stack];
                if (newStack[prev.index]) {
                    newStack[prev.index] = {
                        id: getHistoryItemId(newStack[prev.index]),
                        scrollTop: currentScroll
                    };
                }
                return { ...prev, stack: newStack };
            });
        }
        setViewMode("home");
        if (homeContainerRef.current) {
            homeContainerRef.current.scrollTop = 0;
        }
    };

    const canGoBack = viewMode === "home" ? (navHistory.stack.length > 0 && Boolean(navHistory.stack[navHistory.index])) : navHistory.index > 0;
    const canGoForward = viewMode === "home" ? (navHistory.stack.length > 0 && Boolean(navHistory.stack[navHistory.index])) : navHistory.index < navHistory.stack.length - 1;

    if (!isOpen) return null;

    // Actions
    const handleCopyTerm = (term) => {
        if (!term) return;
        const text = `### ${term.term} [${term.category}]\n\n${term.shortDef}\n\n${term.ruleOrFormula ? `**Rule:** ${term.ruleOrFormula}\n\n` : ''}${term.diagram ? `\`\`\`\n${term.diagram}\n\`\`\`\n\n` : ''}${term.fullDescription}\n\n${term.application ? `*Application:* ${term.application}` : ''}`;
        navigator.clipboard.writeText(text);
        setCopiedId(term.id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleCopyDiagram = (diagram) => {
        if (!diagram) return;
        navigator.clipboard.writeText(diagram);
        setCopiedId("diagram");
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleCopyBlueprint = () => {
        const text = `# GLOBAL SYSTEM ARCHITECTURE & DOCUMENTATION BLUEPRINT

## 01. The Three Core Epistemic Pillars

### 1. Causality vs Description (Liquidity vs Price Quality)
• Liquidity (Market Foundations) = Causal Analysis: Price does not move by magic or due to a chart pattern, but through the mechanical absorption of aggressive market orders into passive limit liquidity and resting stop-loss sweeps. This is the mechanical "Why".
• Price Quality (Market Context) = Descriptive Analysis: Pure, objective observation of candlestick velocity, compression, spread, and momentum, without over-interpreting.
• Logical Bridge: Price Quality objectively describes the graphic footprint; Liquidity provides the underlying causality that drives it.

### 2. Structural Validation (Candidate Pivot vs Confirmed Pivot)
• Candidate Structure (Market Structure - 2): Identifies a Candidate Low in real-time during a retracement into a Demand / AOI zone to calibrate a surgical entry at minimal invalidation distance.
• Confirmed Structure (Market Structure - 1): A pivot only becomes an official Confirmed Primary Low once the opposing structural high (HH) is definitively broken.
• Institutional Alpha: Entering on the Candidate Pivot maximizes asymmetric R:R, while Confirmed Pivots validate the overarching macro trend.

### 3. Levels Triad (Cartography, Order Footprint & Noise Filtering)
• Key Levels: Cardinal horizontal reference mapping (HTF High/Low, Previous Day/Week High/Low, cardinal pivot levels).
• Supply & Demand Zones: Institutional order footprint zones (Impulse Origin, Imbalance, Stop Runs).
• Levels Quality: Anti-noise filter (freshness, touch count history, reaction velocity) to retain only high-probability institutional levels.
• Synergy: Never draw random lines—a level is only actionable if it meets strict Levels Quality criteria.

---

## 02. The Sequential 5-Stage Pipeline (19 Modules)

### Stage 1: Playbook (Systematic Execution & Dynamic Risk Management)
Role: Operational execution: systematic checklist, multi-factor confluence confirmation, active in-trade management, defined invalidation rules, and capital preservation.
Modules:
- [33] Pre-Trade Plan
- [32] Trade Plan
- [30] Trade Plan — Concise
- [31] Trade Management

### Stage 2: Market Foundations (Mechanical Causality & Market Actors)
Role: Understand how orders match in the order book before ever looking at a candlestick chart. Explains passive vs aggressive infrastructure, the 4 market participant categories, and liquidity dynamics.
Modules:
- [20] Order Book
- [21] Who Moves the Market
- [22] Liquidity

### Stage 3: Market Context (Objective Observation, Multi-TF Architecture & Cycles)
Role: The chart translator: establishes the fractal ladder (Step Rule), decodes the chronological AMD price cycle, objectively evaluates price behavior, distinguishes structure (Primary vs Candidate), and isolates trigger mechanics.
Modules:
- [29] Timeframes
- [25] Market Cycles
- [16] Price Quality
- [01] Market Structure
- [15] Market Structure - 2
- [24] Patterns

### Stage 4: Key Price Zones (Institutional Cartography & Level Quality Filtering)
Role: Pinpoint high-probability reaction zones. Combines cardinal horizontal references (Key Levels), institutional order footprints (Supply / Demand), and strict anti-noise criteria (Levels Quality).
Modules:
- [06] Key Levels
- [23] Supply / Demand Zone
- [05] Levels Quality

### Stage 5: Market Confluence (Quantitative Dashboard & Contextual Filters)
Role: Mathematical and statistical validation. Calibrates normal swing sizes vs abnormal institutional expansion (Volatility Grid), verifies broad-market flow participation (Market Internals), and integrates dynamic institutional references (EMAs).
Modules:
- [27] Volatility Grid
- [17] Market Internals
- [26] Price-Derived Metrics

---

## 03. Chronological Live Chart Execution Roadmap
1. Multi-TF Framing (Timeframes — Step Rule & Pacing)
2. Market Cycle (Market Cycles — AMD Phase & Stop Run)
3. Structure & Pivots (Market Structure — Primary HH/HL & Low)
4. Zones & Levels (Key Levels & S/D + Levels Quality)
5. Quantitative Check (Volatility Grid & Market Internals)
6. Trigger & Rejection (Patterns & Price Quality)
7. Trade Plan (Trade Plan — R:R Sizing & Invalidation)
8. Trade Management (Trade Management — Scale-Out & Trailing)`;

        navigator.clipboard.writeText(text);
        setCopiedId("blueprint");
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            setEditingTerm(prev => ({
                ...prev,
                imageUrl: event.target.result,
                imageCaption: prev?.imageCaption || file.name.replace(/\.[^/.]+$/, "")
            }));
        };
        reader.readAsDataURL(file);
    };

    const handleOpenEdit = (term) => {
        setEditingTerm({ ...term });
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingTerm(null);
    };

    const handleDeleteTerm = (id) => {
        if (window.confirm("Are you sure you want to delete this technical definition?")) {
            const updated = terms.filter(t => t.id !== id);
            setTerms(updated);
            setIsEditing(false);
            if (selectedTermId === id) {
                setSelectedTermId(updated[0]?.id || "");
            }
        }
    };

    const handleSaveTerm = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!editingTerm) return;
        if (!editingTerm.term.trim()) {
            alert("Please provide at least a term name.");
            return;
        }

        const tagsArray = typeof editingTerm.tags === 'string'
            ? editingTerm.tags.split(',').map(s => s.trim()).filter(Boolean)
            : editingTerm.tags || [];

        const updatedTerm = {
            ...editingTerm,
            tags: tagsArray,
            isEdited: true
        };

        setTerms(prev => {
            const existingIndex = prev.findIndex(t => t.id === updatedTerm.id);
            let nextTerms;
            if (existingIndex >= 0) {
                nextTerms = [...prev];
                nextTerms[existingIndex] = updatedTerm;
            } else {
                nextTerms = [updatedTerm, ...prev];
            }
            try {
                const defIds = new Set(DEFAULT_DICTIONARY_TERMS.map(d => d.id));
                const customTerms = nextTerms.filter(t => t.isCustom && !defIds.has(t.id));
                if (customTerms.length > 0) {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(customTerms));
                } else {
                    localStorage.removeItem(STORAGE_KEY);
                }
            } catch (err) {
                console.warn("Failed to save terms in localStorage", err);
            }
            return nextTerms;
        });

        setSelectedTermId(updatedTerm.id);
        setIsEditing(false);
        setEditingTerm(null);
    };

    const parseInlineMarkdownForPdf = (text) => {
        if (!text) return "";
        return text
            .replace(/\$\$([\s\S]*?)\$\$/g, (m, eq) => {
                const cleanEq = eq.replace(/\\text\{([^}]+)\}/g, "$1").trim();
                return `<div class="math-box"><code>${cleanEq}</code></div>`;
            })
            .replace(/\$([^$]+)\$/g, (m, eq) => {
                const clean = eq.replace(/\\to/g, "→").replace(/\\text\{([^}]+)\}/g, "$1").trim();
                return `<span class="math-term">${clean}</span>`;
            })
            .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
            .replace(/\*([^*]+)\*/g, "<em>$1</em>")
            .replace(/`([^`]+)`/g, `<code class="inline-code">$1</code>`)
            .replace(/\[([^\]\n]+)\](?!\()/g, (m, tag) => {
                return `<span class="pdf-tag" style="display:inline-block;padding:1px 6px;margin:0 2px;border-radius:4px;font-size:10px;font-weight:600;color:#0284c7;background:#f0f9ff;border:1px solid #bae6fd;">${tag}</span>`;
            });
    };

    const renderPdfApplicationHtml = (appText) => {
        if (!appText) return "";
        if (/^\s*\d+\.\s+\*\*/m.test(appText)) {
            const lines = appText.split(/\n(?=\s*\d+\.\s+\*\*)/);
            let out = "";
            lines.forEach(line => {
                const match = line.match(/^\s*(\d+)\.\s+\*\*([^*]+)\*\*[:\s]*(.*)/s);
                if (match) {
                    out += `
                        <div class="strategy-step">
                            <div class="strategy-step-badge">${match[1]}</div>
                            <div class="strategy-step-content">
                                <strong>${match[2].trim()}</strong>: ${parseInlineMarkdownForPdf(match[3].trim())}
                            </div>
                        </div>
                    `;
                } else {
                    out += `<p class="strategy-paragraph">${parseInlineMarkdownForPdf(line)}</p>`;
                }
            });
            return out;
        }
        return `<p class="strategy-paragraph">${parseInlineMarkdownForPdf(appText)}</p>`;
    };

    const renderPdfContentHtml = (content, term) => {
        if (!content) return "";

        const hasHeadings = content.includes("### ");
        const termImages = (term.images && term.images.length > 0)
            ? term.images
            : (term.imageUrl ? [{ url: term.imageUrl, caption: term.imageCaption || "" }] : []);
        const hasImages = termImages.length > 0;

        const isInsertBeforeKeyLevels = Boolean(
            hasImages && (
                term.id === "market_structure" ||
                term.id === "market_structure_logic" ||
                term.id === "external_vs_internal_structure" ||
                /primary structure and key price levels/i.test(content)
            )
        );

        const renderIllustrationCards = () => {
            if (!hasImages) return "";
            return termImages.map((img, i) => `
                <div class="illustration-card" style="margin: 14px 0;">
                    <div class="illustration-header">
                        <div class="illustration-title-group">
                            <span class="ill-badge">📊</span>
                            <span class="ill-title">Illustration ${termImages.length > 1 ? `#${i + 1} &bull; ` : ""}${img.caption ? "" : "Chart Reference"}</span>
                        </div>
                        ${img.caption ? `<span class="ill-caption">${img.caption}</span>` : ""}
                    </div>
                    <div class="illustration-body">
                        <img src="${img.url}" alt="${img.caption || term.term}" class="illustration-img" />
                    </div>
                </div>
            `).join("");
        };

        let html = "";

        if (hasHeadings) {
            const rawSections = content.split(/(?=(?:^|\n)#{2,3} (?!#))/g).map(s => s.trim()).filter(Boolean);
            const badgeGradients = [
                "linear-gradient(135deg, #10b981, #059669)",
                "linear-gradient(135deg, #0ea5e9, #0284c7)",
                "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                "linear-gradient(135deg, #f59e0b, #d97706)",
                "linear-gradient(135deg, #ec4899, #be185d)"
            ];

            rawSections.forEach((secStr, secIdx) => {
                let title = "";
                let body = secStr;
                const prefixLen = secStr.startsWith('### ') ? 4 : (secStr.startsWith('## ') ? 3 : 0);
                const firstNewline = secStr.indexOf("\n");
                if (firstNewline !== -1) {
                    title = secStr.substring(prefixLen, firstNewline).trim();
                    body = secStr.substring(firstNewline + 1).trim();
                } else {
                    title = secStr.substring(prefixLen).trim();
                    body = "";
                }

                let numLabel = String(secIdx + 1).padStart(2, "0");
                let cleanTitle = title;
                const numMatch = title.match(/^((?:PART\s+[IVXLCDM]+|[IVXLCDM]+|\d+(?:\.\d+)?))[.\s—–-]+(.*)/i) || title.match(/^(\d+)[.\s]+(.*)/);
                if (numMatch) {
                    numLabel = String(numMatch[1]).replace(/^PART\s+/i, '').padStart(2, "0");
                    cleanTitle = numMatch[2].trim();
                }

                const isBeforeThis = isInsertBeforeKeyLevels && cleanTitle.toLowerCase().includes("primary structure and key price levels");
                if (isBeforeThis) {
                    html += renderIllustrationCards();
                }

                const badgeBg = badgeGradients[secIdx % badgeGradients.length];

                html += `<div class="concept-card">`;
                html += `
                    <div class="concept-header">
                        <div class="concept-badge" style="background: ${badgeBg};">${numLabel}</div>
                        <div class="concept-title">${formatTitleWithSubtitle(cleanTitle).replace(/\*([^*]+)\*/g, '<em>$1</em>')}</div>
                    </div>
                `;
                html += `<div class="concept-body">`;

                const blocks = body.split(/\n\s*\n/).map(b => b.trim()).filter(b => b && !b.match(/^---+$/));

                blocks.forEach(block => {
                    if (block.includes("HH + HL = Uptrend")) {
                        html += `
                            <div class="formula-pill">
                                <span class="pill-icon">⚡</span>
                                <span class="pill-text">HH + HL = Uptrend</span>
                            </div>
                        `;
                        return;
                    }

                    if (/^\*{0,2}key\s+principle:?\*{0,2}$/i.test(block.trim())) {
                        return;
                    }

                    if (/^\*{0,2}key\s+principle:?\*{0,2}/i.test(block.trim())) {
                        if (block.includes("Primary High/Low →") && block.includes("Horizontal Level →")) {
                            html += `
                                <div class="confluence-card">
                                    <div class="confluence-header">
                                        <span class="conf-icon">⚡</span>
                                        <span>Key Principle &amp; Structural Confluence</span>
                                    </div>
                                    <div class="confluence-row">
                                        <span class="conf-pill conf-blue">Primary High / Low</span>
                                        <span class="conf-arrow">→</span>
                                        <span class="conf-text">Defines overall trend structure</span>
                                    </div>
                                    <div class="confluence-row">
                                        <span class="conf-pill conf-purple">Horizontal Level</span>
                                        <span class="conf-arrow">→</span>
                                        <span class="conf-text">Defines stronger institutional price significance</span>
                                    </div>
                                    <div class="confluence-row conf-highlight">
                                        <span class="conf-pill conf-gold">Primary Pivot + Horizontal</span>
                                        <span class="conf-arrow">→</span>
                                        <span class="conf-gold-text">⭐ Strongest Structural Confluence &amp; High-Probability Reaction</span>
                                    </div>
                                </div>
                            `;
                            return;
                        }

                        const principleText = block.replace(/^\*{0,2}key\s+principle:?\*{0,2}\s*/i, '').trim();
                        if (principleText) {
                            html += `
                                <div class="confluence-card" style="border-left: 4px solid #f59e0b; padding: 12px 16px;">
                                    <div class="confluence-header">
                                        <span class="conf-icon">⚡</span>
                                        <span style="font-weight: 700; color: #854d0e;">Key Principle</span>
                                    </div>
                                    <p style="font-size: 12.5px; font-weight: 600; color: #854d0e; margin: 4px 0 0 0;">${parseInlineMarkdownForPdf(principleText)}</p>
                                </div>
                            `;
                            return;
                        }
                    }

                    if (/^(?:>\s*)?(?:⚠️\s*)?\*{0,2}(?:caution|attention|warning):?\*{0,2}/i.test(block.trim()) || block.startsWith('> ⚠️') || block.startsWith('> Caution') || block.startsWith('> **Caution')) {
                        const cleanCaution = block
                            .replace(/^>\s*/, '')
                            .replace(/^⚠️\s*/, '')
                            .replace(/^\*{0,2}(?:caution|attention|warning):?\*{0,2}\s*/i, '')
                            .trim();
                        html += `
                            <div class="invalidation-alert" style="background: #fffbeb; border: 1px solid #fde68a; color: #92400e; margin: 8px 0;">
                                <span class="alert-icon">⚠️</span>
                                <div><strong>Caution:</strong> ${parseInlineMarkdownForPdf(cleanCaution)}</div>
                            </div>
                        `;
                        return;
                    }

                    const trimmedBlock = block.trim();
                    if (trimmedBlock.startsWith('|') && trimmedBlock.includes('---')) {
                        const lines = trimmedBlock.split('\n').map(l => l.trim()).filter(Boolean);
                        if (lines.length >= 2 && lines[0].startsWith('|') && lines[1].includes('---')) {
                            const parseRow = (line) => line.split(/(?<!\\)\|/).slice(1, -1).map(c => c.replace(/\\\|/g, '|').trim());
                            const headers = parseRow(lines[0]);
                            const rows = lines.slice(2).map(parseRow);
                            html += `
                                <div class="pdf-table-wrapper">
                                    <table class="pdf-doc-table">
                                        <thead>
                                            <tr>
                                                ${headers.map(h => `<th>${parseInlineMarkdownForPdf(h)}</th>`).join('')}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            ${rows.map(row => `
                                                <tr>
                                                    ${row.map(cell => `<td>${parseInlineMarkdownForPdf(cell)}</td>`).join('')}
                                                </tr>
                                            `).join('')}
                                        </tbody>
                                    </table>
                                </div>
                            `;
                            return;
                        }
                    }

                    const ruleCardMatch = block.match(/^\s*\*\*(\d+)\s*[—–-]\s*([^*]+)\*\*(?::)?\s*([\s\S]*)/);
                    if (ruleCardMatch) {
                        const [, rNum, rHeading, rBody] = ruleCardMatch;
                        html += `
                            <div class="step-card">
                                <div class="step-badge">${rNum}</div>
                                <div class="step-content" style="flex: 1;">
                                    <div class="step-title">${parseInlineMarkdownForPdf(rHeading.trim())}</div>
                                    ${rBody.trim() ? `<div class="step-body">${parseInlineMarkdownForPdf(rBody.trim())}</div>` : ''}
                                </div>
                            </div>
                        `;
                        return;
                    }

                    if (block.includes("**Primary Low**") || /^\s*[-*]?\s*Primary Low:/i.test(block)) {
                        const cleanLow = block.replace(/^[-*]?\s*\*\*Primary Low\*\*:\s*/i, '').replace(/^[-*]?\s*Primary Low:?\s*/i, '').trim();
                        const hasInvalidation = cleanLow.includes("invalidated") || cleanLow.includes("Structural Shift");
                        let mainLowDesc = cleanLow;
                        let invalidationSentence = "";
                        if (hasInvalidation) {
                            const lines = cleanLow.split("\n");
                            const invLines = [];
                            const descLines = [];
                            lines.forEach(l => {
                                if (l.includes("invalidated") || l.includes("Structural Shift")) invLines.push(l);
                                else descLines.push(l);
                            });
                            invalidationSentence = invLines.join(" ").trim();
                            mainLowDesc = descLines.join(" ").trim();
                        }
                        html += `
                            <div class="pivot-card pivot-green-card">
                                <div class="pivot-header">
                                    <span class="pivot-tag pivot-tag-green">● Primary Low (Key Floor)</span>
                                </div>
                                <p class="pivot-desc">${parseInlineMarkdownForPdf(mainLowDesc || cleanLow)}</p>
                                ${invalidationSentence ? `
                                    <div class="invalidation-alert">
                                        <span class="alert-icon">⚠️</span>
                                        <div>${parseInlineMarkdownForPdf(invalidationSentence)}</div>
                                    </div>
                                ` : ""}
                            </div>
                        `;
                        return;
                    }

                    if (block.includes("**Primary High**") || /^\s*[-*]?\s*Primary High:/i.test(block)) {
                        const cleanHigh = block.replace(/^[-*]?\s*\*\*Primary High\*\*:\s*/i, '').replace(/^[-*]?\s*Primary High:?\s*/i, '').trim();
                        html += `
                            <div class="pivot-card pivot-blue-card">
                                <div class="pivot-header">
                                    <span class="pivot-tag pivot-tag-blue">● Primary High (Key Target)</span>
                                </div>
                                <p class="pivot-desc">${parseInlineMarkdownForPdf(cleanHigh)}</p>
                            </div>
                        `;
                        return;
                    }

                    if (!block.startsWith('#') && !/^\s*\d+\.\s+/.test(block) && (/^\s*[-*]?\s*\*\*(?:Temporal Consistency|Temporal Symmetry|Multi-TF Rule|Multi-Timeframe Rule)\*\*/i.test(block) || /^\s*[-*]?\s*(?:Temporal Consistency|Temporal Symmetry|Multi-TF Rule|Multi-Timeframe Rule):/i.test(block) || /^\s*[-*]?\s*Duration Consistency Rule:/i.test(block))) {
                        const titleMatch = block.match(/\*\*([^*]+)\*\*/);
                        const ruleTitle = titleMatch ? titleMatch[1].trim() : (block.toLowerCase().includes('multi-tf') ? 'Multi-TF Rule' : 'Temporal Consistency');
                        const isMultiTf = block.toLowerCase().includes('multi-tf') || block.toLowerCase().includes('timeframe');
                        const badgeIcon = isMultiTf ? '🪜' : '⏱️';
                        const cleanRule = block
                            .replace(/^[-*]?\s*\*\*[^*]+\*\*:\s*/i, '')
                            .replace(/^[-*]?\s*(?:Temporal|Multi-TF|Multi-Timeframe)[^:]*:?\s*/i, '')
                            .trim();
                        const ruleLines = (cleanRule || block).split('\n').map(l => l.trim()).filter(Boolean);
                        const mainRule = ruleLines[0] || '';
                        const subRuleLines = ruleLines.slice(1);
                        let subHtml = '';
                        if (subRuleLines.length > 0) {
                            subHtml = `<ul style="margin: 8px 0 0 16px; padding: 0; font-size: 0.88rem; color: #cbd5e1;">${subRuleLines.map(s => `<li>${parseInlineMarkdownForPdf(s.replace(/^[-*]\s*/, ''))}</li>`).join('')}</ul>`;
                        }
                        html += `
                            <div class="pivot-card pivot-purple-card">
                                <div class="pivot-header">
                                    <span class="pivot-tag pivot-tag-purple">${badgeIcon} ${ruleTitle}</span>
                                </div>
                                <p class="pivot-desc">${parseInlineMarkdownForPdf(mainRule)}</p>
                                ${subHtml}
                            </div>
                        `;
                        return;
                    }

                    if (/^\s*\d+\.\s+/.test(block)) {
                        const lines = block.split("\n").filter(l => l.trim());
                        html += `<ol class="styled-list">`;
                        lines.forEach(l => {
                            const numMatch = l.match(/^\s*(\d+)\.\s+(.*)/);
                            const bulletMatch = l.match(/^\s*[-*]\s+(.*)/);
                            if (numMatch) {
                                html += `<li value="${numMatch[1]}">${parseInlineMarkdownForPdf(numMatch[2])}</li>`;
                            } else if (bulletMatch) {
                                html += `<li style="list-style-type: circle; margin-left: 18px; margin-top: 3px;">${parseInlineMarkdownForPdf(bulletMatch[1])}</li>`;
                            } else {
                                html += `<div style="margin-left: 18px;">${parseInlineMarkdownForPdf(l)}</div>`;
                            }
                        });
                        html += `</ol>`;
                        return;
                    }

                    if (/^\s*[-*]\s+/m.test(block)) {
                        const lines = block.split("\n").filter(l => l.trim());
                        html += `<ul class="styled-list">`;
                        lines.forEach(l => {
                            const cleanLine = l.replace(/^\s*[-*]\s+/, "");
                            html += `<li>${parseInlineMarkdownForPdf(cleanLine)}</li>`;
                        });
                        html += `</ul>`;
                        return;
                    }

                    const imgMatches = [...block.matchAll(/!\[(.*?)\]\((.*?)\)/g)];
                    if (imgMatches.length > 0) {
                        imgMatches.forEach(m => {
                            html += `
                                <div class="illustration-card" style="margin: 14px 0;">
                                    <div class="illustration-header">
                                        <div class="illustration-title-group">
                                            <span class="ill-badge">📊</span>
                                            <span class="ill-title">${m[1] || "Illustration"}</span>
                                        </div>
                                    </div>
                                    <div class="illustration-body">
                                        <img src="${m[2]}" alt="${m[1] || ""}" class="illustration-img" />
                                    </div>
                                </div>
                            `;
                        });
                        return;
                    }

                    html += `<p class="concept-paragraph">${parseInlineMarkdownForPdf(block)}</p>`;
                });

                html += `</div></div>`;
            });
        } else {
            const blocks = content.split(/\n\s*\n/).map(b => b.trim()).filter(b => b && !b.match(/^---+$/));
            html += `<div class="concept-card"><div class="concept-body">`;
            blocks.forEach(block => {
                const trimmedBlock = block.trim();
                if (trimmedBlock.startsWith('|') && trimmedBlock.includes('---')) {
                    const lines = trimmedBlock.split('\n').map(l => l.trim()).filter(Boolean);
                    if (lines.length >= 2 && lines[0].startsWith('|') && lines[1].includes('---')) {
                        const parseRow = (line) => line.split(/(?<!\\)\|/).slice(1, -1).map(c => c.replace(/\\\|/g, '|').trim());
                        const headers = parseRow(lines[0]);
                        const rows = lines.slice(2).map(parseRow);
                        html += `
                            <div class="pdf-table-wrapper">
                                <table class="pdf-doc-table">
                                    <thead>
                                        <tr>
                                            ${headers.map(h => `<th>${parseInlineMarkdownForPdf(h)}</th>`).join('')}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${rows.map(row => `
                                            <tr>
                                                ${row.map(cell => `<td>${parseInlineMarkdownForPdf(cell)}</td>`).join('')}
                                            </tr>
                                        `).join('')}
                                    </tbody>
                                </table>
                            </div>
                        `;
                        return;
                    }
                }

                if (/^\s*\d+\.\s+\*\*/m.test(block)) {
                    const lines = block.split(/\n(?=\s*\d+\.\s+\*\*)/);
                    lines.forEach(line => {
                        const match = line.match(/^\s*(\d+)\.\s+\*\*([^*]+)\*\*[:\s]*(.*)/s);
                        if (match) {
                            html += `
                                <div class="step-card">
                                    <div class="step-badge">${match[1]}</div>
                                    <div class="step-content">
                                        <div class="step-title">${match[2].trim()}</div>
                                        <div class="step-body">${parseInlineMarkdownForPdf(match[3].trim())}</div>
                                    </div>
                                </div>
                            `;
                        } else {
                            html += `<p class="concept-paragraph">${parseInlineMarkdownForPdf(line)}</p>`;
                        }
                    });
                    return;
                }

                if (/^\s*\d+\.\s+/.test(block)) {
                    const lines = block.split("\n").filter(l => l.trim());
                    html += `<ol class="styled-list">`;
                    lines.forEach(l => {
                        const numMatch = l.match(/^\s*(\d+)\.\s+(.*)/);
                        const bulletMatch = l.match(/^\s*[-*]\s+(.*)/);
                        if (numMatch) {
                            html += `<li value="${numMatch[1]}">${parseInlineMarkdownForPdf(numMatch[2])}</li>`;
                        } else if (bulletMatch) {
                            html += `<li style="list-style-type: circle; margin-left: 18px; margin-top: 3px;">${parseInlineMarkdownForPdf(bulletMatch[1])}</li>`;
                        } else {
                            html += `<div style="margin-left: 18px;">${parseInlineMarkdownForPdf(l)}</div>`;
                        }
                    });
                    html += `</ol>`;
                    return;
                }

                if (/^\s*[-*]\s+/m.test(block)) {
                    const lines = block.split("\n").filter(l => l.trim());
                    html += `<ul class="styled-list">`;
                    lines.forEach(l => {
                        const cleanLine = l.replace(/^\s*[-*]\s+/, "");
                        html += `<li>${parseInlineMarkdownForPdf(cleanLine)}</li>`;
                    });
                    html += `</ul>`;
                    return;
                }

                const imgMatches = [...block.matchAll(/!\[(.*?)\]\((.*?)\)/g)];
                if (imgMatches.length > 0) {
                    imgMatches.forEach(m => {
                        html += `
                            <div class="illustration-card" style="margin: 14px 0;">
                                <div class="illustration-header">
                                    <div class="illustration-title-group">
                                        <span class="ill-badge">📊</span>
                                        <span class="ill-title">${m[1] || "Illustration"}</span>
                                    </div>
                                </div>
                                <div class="illustration-body">
                                    <img src="${m[2]}" alt="${m[1] || ""}" class="illustration-img" />
                                </div>
                            </div>
                        `;
                    });
                    return;
                }

                html += `<p class="concept-paragraph">${parseInlineMarkdownForPdf(block)}</p>`;
            });
            html += `</div></div>`;
        }

        if (hasImages && !isInsertBeforeKeyLevels && !content.includes("![")) {
            html += renderIllustrationCards();
        }

        return html;
    };

    const renderSingleDocPdfBody = (term, index = 0, total = 1) => {
        return `
            <article class="doc-section-page" id="doc-${term.id}">
                <div class="top-header">
                    <div class="brand-group">
                        <span class="brand-badge">Trade Terminal</span>
                        <span class="brand-title">Market Vision</span>
                        <span class="brand-sub">&bull; Technical Reference</span>
                    </div>
                    <div class="header-meta">
                        <div class="doc-id">REF: ${(term.id || "DOC").toUpperCase()}${total > 1 ? ` &bull; Module ${index + 1}/${total}` : ""}</div>
                        <div>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                    </div>
                </div>

                <div class="hero-section">
                    <div class="cat-badge">● ${term.category || "Market Context"}</div>
                    <h1 class="hero-title">${term.term}</h1>
                    <div class="tags-row">
                        ${(term.tags || []).map(t => `<span class="tag-pill">#${t}</span>`).join(" ")}
                    </div>
                </div>

                ${term.shortDef ? `
                    <div class="summary-card">
                        <div class="summary-header">
                            <span>💡</span>
                            <span>Executive Summary &bull; Definition</span>
                        </div>
                        <p class="summary-text">${parseInlineMarkdownForPdf(term.shortDef)}</p>
                    </div>
                ` : ""}

                ${term.ruleOrFormula ? `
                    <div class="rule-card">
                        <div class="rule-header">
                            <span>⚡</span>
                            <span>Institutional Rule &amp; Quantitative Formula</span>
                        </div>
                        <div class="rule-code">${term.ruleOrFormula}</div>
                    </div>
                ` : ""}

                ${term.fullDescription ? `
                    <div class="section-wrapper">
                        <div class="section-main-heading">
                            <span>📖</span>
                            <span>Core Principles &amp; Technical Breakdown</span>
                        </div>
                        ${renderPdfContentHtml(term.fullDescription, term)}
                    </div>
                ` : ""}

                ${term.application ? `
                    <div class="strategy-card">
                        <div class="strategy-header">
                            <span>🎯</span>
                            <span>Strategic Application &amp; Execution Playbook</span>
                        </div>
                        <div class="strategy-body">
                            ${renderPdfApplicationHtml(term.application)}
                        </div>
                    </div>
                ` : ""}

                <div class="footer-bar">
                    <div>Document Ref: ${term.id}${total > 1 ? ` &bull; Module ${index + 1} of ${total}` : ""} &bull; Trade Terminal Market Vision Quantitative Engine</div>
                    <div>CONFIDENTIAL &bull; FOR TRADING SYSTEM EXECUTION ONLY</div>
                </div>
            </article>
        `;
    };

    const handleExportPdf = (term) => {
        if (!term) return;

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        iframe.setAttribute("aria-hidden", "true");
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="utf-8" />
                <title>${(term.term || "Concept").replace(/[^a-zA-Z0-9_\-\s]/g, "")} - Technical Reference</title>
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600;700&display=swap" rel="stylesheet">
                <style>
                    ${PDF_DOCUMENT_STYLES}
                </style>
            </head>
            <body>
                ${renderSingleDocPdfBody(term, 0, 1)}
            </body>
            </html>
        `);
        doc.close();

        const triggerPrint = () => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                }
            }, 2500);
        };

        const images = doc.images;
        if (!images || images.length === 0) {
            setTimeout(triggerPrint, 350);
        } else {
            let loadedCount = 0;
            const total = images.length;
            const onDone = () => {
                loadedCount++;
                if (loadedCount >= total) {
                    setTimeout(triggerPrint, 250);
                }
            };
            for (let i = 0; i < total; i++) {
                if (images[i].complete) {
                    onDone();
                } else {
                    images[i].addEventListener("load", onDone);
                    images[i].addEventListener("error", onDone);
                }
            }
            setTimeout(() => {
                if (loadedCount < total) {
                    triggerPrint();
                }
            }, 1200);
        }
    };

    const handleExportAllPdf = () => {
        if (isExportingAllPdf) return;
        setIsExportingAllPdf(true);

        const categoryMap = new Map();
        DEFAULT_DICTIONARY_CATEGORIES.forEach(cat => {
            categoryMap.set(cat, []);
        });

        terms.forEach(term => {
            const cat = term.category || "Market Context";
            if (!categoryMap.has(cat)) {
                categoryMap.set(cat, []);
            }
            categoryMap.get(cat).push(term);
        });

        const categoriesWithTerms = [];
        const orderedTerms = [];
        categoryMap.forEach((termsList, catName) => {
            if (termsList.length > 0) {
                categoriesWithTerms.push({
                    name: catName,
                    terms: termsList
                });
                orderedTerms.push(...termsList);
            }
        });

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        iframe.setAttribute("aria-hidden", "true");
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();

        const currentDateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const totalDocs = orderedTerms.length;
        const totalCategories = categoriesWithTerms.length;

        doc.write(`
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="utf-8" />
                <title>Trade Terminal - Complete Technical Reference Manual (${totalDocs} Modules)</title>
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600;700&display=swap" rel="stylesheet">
                <style>
                    ${PDF_DOCUMENT_STYLES}
                </style>
            </head>
            <body>
                <!-- COVER & TABLE OF CONTENTS PAGE -->
                <div class="cover-page">
                    <div>
                        <div class="cover-brand-header">
                            <div class="cover-badge">Trade Terminal &bull; Market Vision</div>
                            <div class="cover-date">${currentDateStr}</div>
                        </div>

                        <div class="cover-main">
                            <h1 class="cover-title">Quantitative Market Vision</h1>
                            <h2 class="cover-subtitle">Complete Technical Reference &amp; Execution Manual</h2>
                            <div class="cover-divider"></div>
                            <p class="cover-desc">
                                Institutional handbook covering market structure mechanics, liquidity dynamics,
                                algorithmic execution models, volatility grids, statistical arbitrage, and cross-asset metrics.
                            </p>

                            <div class="cover-stats-grid">
                                <div class="cover-stat-card">
                                    <span class="cover-stat-val">${totalDocs}</span>
                                    <span class="cover-stat-lbl">Core Modules</span>
                                </div>
                                <div class="cover-stat-card">
                                    <span class="cover-stat-val">${totalCategories}</span>
                                    <span class="cover-stat-lbl">Categories</span>
                                </div>
                                <div class="cover-stat-card">
                                    <span class="cover-stat-val">Quantitative</span>
                                    <span class="cover-stat-lbl">Grade</span>
                                </div>
                            </div>
                        </div>

                        <div class="toc-container">
                            <div class="toc-title">Table of Contents &bull; Documentation Modules</div>
                            <div class="toc-grid">
                                ${categoriesWithTerms.map((cat, cIdx) => `
                                    <div class="toc-category-block">
                                        <div class="toc-category-name">${cIdx + 1}. ${cat.name}</div>
                                        <ul class="toc-list">
                                            ${cat.terms.map(t => `
                                                <li class="toc-item">
                                                    <span class="toc-term-name">${t.term}</span>
                                                    <span class="toc-term-id">${(t.id || "").toUpperCase()}</span>
                                                </li>
                                            `).join("")}
                                        </ul>
                                    </div>
                                `).join("")}
                            </div>
                        </div>
                    </div>

                    <div class="cover-footer">
                        <div>Trade Terminal &bull; Market Vision Quantitative Engine</div>
                        <div>CONFIDENTIAL &bull; FOR TRADING SYSTEM EXECUTION ONLY</div>
                    </div>
                </div>

                <!-- ALL DOCUMENTATION MODULES -->
                ${orderedTerms.map((t, idx) => renderSingleDocPdfBody(t, idx, totalDocs)).join("")}
            </body>
            </html>
        `);
        doc.close();

        const triggerPrint = () => {
            setIsExportingAllPdf(false);
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                }
            }, 3000);
        };

        const images = doc.images;
        if (!images || images.length === 0) {
            setTimeout(triggerPrint, 400);
        } else {
            let loadedCount = 0;
            const total = images.length;
            const onDone = () => {
                loadedCount++;
                if (loadedCount >= total) {
                    setTimeout(triggerPrint, 350);
                }
            };
            for (let i = 0; i < total; i++) {
                if (images[i].complete) {
                    onDone();
                } else {
                    images[i].addEventListener("load", onDone);
                    images[i].addEventListener("error", onDone);
                }
            }
            setTimeout(() => {
                if (loadedCount < total) {
                    triggerPrint();
                }
            }, 2200);
        }
    };

    const renderIllustrationBlock = () => {
        if (!activeTerm || activeImages.length === 0 || !currentIllustration) return null;
        return (
            <DocSectionCard style={{ marginBottom: '16px' }}>
                <DocSectionHeader>
                    <DocSectionBadge badgeBg="linear-gradient(135deg, #06b6d4, #0ea5e9)">
                        <FiImage size={15} color="#ffffff" />
                    </DocSectionBadge>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, flexWrap: 'wrap', gap: '8px' }}>
                        <DocSectionTitle style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span>Illustration</span>
                            {currentIllustration.caption && (
                                <span style={{
                                    fontSize: '0.82rem',
                                    fontWeight: '500',
                                    color: '#94a3b8',
                                    background: 'rgba(255, 255, 255, 0.04)',
                                    padding: '2px 8px',
                                    borderRadius: '5px',
                                    border: '1px solid rgba(255, 255, 255, 0.07)'
                                }}>
                                    {currentIllustration.caption}
                                </span>
                            )}
                        </DocSectionTitle>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {activeImages.length > 1 && (
                                <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    padding: '3px 8px',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)'
                                }}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePrevImage();
                                        }}
                                        title="Image précédente (<)"
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.08)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#e2e8f0',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            padding: 0
                                        }}
                                    >
                                        <FiChevronLeft size={14} />
                                    </button>
                                    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#38bdf8', minWidth: '38px', textAlign: 'center' }}>
                                        {currentIllustrationIndex + 1} / {activeImages.length}
                                    </span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleNextImage();
                                        }}
                                        title="Image suivante (>)"
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.08)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#e2e8f0',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            padding: 0
                                        }}
                                    >
                                        <FiChevronRight size={14} />
                                    </button>
                                </div>
                            )}

                            <button
                                onClick={() => setLightboxImage(currentIllustration.url)}
                                title="Plein écran"
                                aria-label="Plein écran"
                                style={{
                                    background: 'rgba(76, 206, 172, 0.12)',
                                    border: '1px solid rgba(76, 206, 172, 0.3)',
                                    color: '#4cceac',
                                    borderRadius: '7px',
                                    width: '30px',
                                    height: '30px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <FiMaximize2 size={14} />
                            </button>
                        </div>
                    </div>
                </DocSectionHeader>

                <ImageWrapper
                    onClick={() => setLightboxImage(currentIllustration.url)}
                    title="Cliquer pour agrandir"
                    style={{
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        background: '#040712'
                    }}
                >
                    <img src={currentIllustration.url} alt={currentIllustration.caption || activeTerm.term} />
                </ImageWrapper>

                {currentIllustration.caption && (
                    <ImageCaptionBar style={{
                        marginTop: '8px',
                        borderRadius: '6px',
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.04)'
                    }}>
                        <span>📌</span>
                        <span>{currentIllustration.caption}</span>
                    </ImageCaptionBar>
                )}
            </DocSectionCard>
        );
    };

    const renderSystemBlueprint = () => {
        return (
            <BlueprintContainer>
                {/* 01. 3 Epistemic Pillars */}
                <BlueprintTopRow>
                    <BlueprintSectionTitle style={{ margin: 0 }}>
                        <span>01. The Three Core Epistemic Pillars</span>
                    </BlueprintSectionTitle>
                    <BlueprintCopyBtn
                        onClick={handleCopyBlueprint}
                        title={copiedId === "blueprint" ? "Copied to clipboard!" : "Copy description"}
                        aria-label="Copy description"
                    >
                        {copiedId === "blueprint" ? <FiCheck size={14} color="#10b981" /> : <FiCopy size={14} />}
                    </BlueprintCopyBtn>
                </BlueprintTopRow>

                <BlueprintPillarsGrid>
                    {/* Pillar 1 */}
                    <BlueprintPillarCard>
                        <PillarHeader>
                            <PillarTag $variant="amber">Causality vs Description</PillarTag>
                            <PillarTitle>Why Price Moves vs What We Objectively Observe</PillarTitle>
                        </PillarHeader>
                        <PillarBody>
                            <div className="pillar-bullet">
                                <strong>Liquidity (Market Foundations) = Causal Analysis:</strong> Price does not move by magic or due to a chart pattern, but through the mechanical absorption of aggressive market orders into passive limit liquidity and resting stop-loss sweeps. This is the mechanical <em>"Why"</em>.
                            </div>
                            <div className="pillar-bullet">
                                <strong>Price Quality (Market Context) = Descriptive Analysis:</strong> Pure, objective observation of candlestick velocity, compression, spread, and momentum, <em>without over-interpreting</em>.
                            </div>
                            <PillarHighlight>
                                <strong>Logical Bridge:</strong> Price Quality objectively describes the graphic footprint; Liquidity provides the underlying causality that drives it.
                            </PillarHighlight>
                        </PillarBody>
                    </BlueprintPillarCard>

                    {/* Pillar 2 */}
                    <BlueprintPillarCard>
                        <PillarHeader>
                            <PillarTag $variant="cyan">Structural Validation</PillarTag>
                            <PillarTitle>Candidate Pivot (Real-Time) vs Confirmed Pivot (Trend)</PillarTitle>
                        </PillarHeader>
                        <PillarBody>
                            <div className="pillar-bullet">
                                <strong>Candidate Structure (Market Structure - 2):</strong> Identifies a <em>Candidate Low</em> in real-time during a retracement into a Demand / AOI zone to calibrate a surgical entry at minimal invalidation distance.
                            </div>
                            <div className="pillar-bullet">
                                <strong>Confirmed Structure (Market Structure - 1):</strong> A pivot only becomes an official <em>Confirmed Primary Low</em> once the opposing structural high (HH) is definitively broken.
                            </div>
                            <PillarHighlight>
                                <strong>Institutional Alpha:</strong> Entering on the Candidate Pivot maximizes asymmetric R:R, while Confirmed Pivots validate the overarching macro trend.
                            </PillarHighlight>
                        </PillarBody>
                    </BlueprintPillarCard>

                    {/* Pillar 3 */}
                    <BlueprintPillarCard>
                        <PillarHeader>
                            <PillarTag $variant="emerald">Levels Triad</PillarTag>
                            <PillarTitle>Cartography, Order Footprint & Noise Filtering</PillarTitle>
                        </PillarHeader>
                        <PillarBody>
                            <div className="pillar-bullet">
                                <strong>Key Levels:</strong> Cardinal horizontal reference mapping (HTF High/Low, Previous Day/Week High/Low, cardinal pivot levels).
                            </div>
                            <div className="pillar-bullet">
                                <strong>Supply & Demand Zones:</strong> Institutional order footprint zones (Impulse Origin, Imbalance, Stop Runs).
                            </div>
                            <div className="pillar-bullet">
                                <strong>Levels Quality:</strong> Anti-noise filter (freshness, touch count history, reaction velocity) to retain only high-probability institutional levels.
                            </div>
                            <PillarHighlight>
                                <strong>Synergy:</strong> Never draw random lines—a level is only actionable if it meets strict Levels Quality criteria.
                            </PillarHighlight>
                        </PillarBody>
                    </BlueprintPillarCard>
                </BlueprintPillarsGrid>

                {/* 02. 5 Sequential Stages */}
                <BlueprintSectionTitle style={{ marginTop: '20px' }}>
                    <span>02. The Sequential 5-Stage Pipeline (19 Modules)</span>
                    <span style={{ fontSize: '0.74rem', fontWeight: 400, opacity: 0.7 }}>Click on any module to open its full documentation directly</span>
                </BlueprintSectionTitle>

                <BlueprintStagesWrapper>
                    {/* Stage 1 - Playbook */}
                    <StageCard $borderAccent="#f43f5e">
                        <StageHeader>
                            <StageNumber $bg="rgba(244, 63, 94, 0.15)" $color="#f43f5e">01</StageNumber>
                            <StageTitleWrapper>
                                <StageTitle>Playbook</StageTitle>
                                <StageTagline>Systematic Execution & Dynamic Risk Management</StageTagline>
                            </StageTitleWrapper>
                        </StageHeader>
                        <StageRoleDesc>
                            Operational execution: systematic checklist, multi-factor confluence confirmation, active in-trade management, defined invalidation rules, and capital preservation.
                        </StageRoleDesc>
                        <StageDocChips>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("pre_trade_plan")} title="Open Pre-Trade Plan">
                                <span className="doc-num">33</span>
                                <span className="doc-name">Pre-Trade Plan</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("trade_plan")} title="Open Trade Plan">
                                <span className="doc-num">32</span>
                                <span className="doc-name">Trade Plan</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("trade_plan_concise")} title="Open Trade Plan — Concise">
                                <span className="doc-num">30</span>
                                <span className="doc-name">Trade Plan — Concise</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("trade_management")} title="Open Trade Management">
                                <span className="doc-num">31</span>
                                <span className="doc-name">Trade Management</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                        </StageDocChips>
                    </StageCard>

                    {/* Stage 2 - Market Foundations */}
                    <StageCard $borderAccent="#f59e0b">
                        <StageHeader>
                            <StageNumber $bg="rgba(245, 158, 11, 0.15)" $color="#f59e0b">02</StageNumber>
                            <StageTitleWrapper>
                                <StageTitle>Market Foundations</StageTitle>
                                <StageTagline>Mechanical Causality & Market Actors (The "Why")</StageTagline>
                            </StageTitleWrapper>
                        </StageHeader>
                        <StageRoleDesc>
                            Understand how orders match in the order book before ever looking at a candlestick chart. Explains passive vs aggressive infrastructure, the 4 market participant categories, and liquidity dynamics.
                        </StageRoleDesc>
                        <StageDocChips>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("order_book")} title="Open Order Book">
                                <span className="doc-num">20</span>
                                <span className="doc-name">Order Book</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("market_flows")} title="Open Who Moves the Market">
                                <span className="doc-num">21</span>
                                <span className="doc-name">Who Moves the Market</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("liquidity")} title="Open Liquidity">
                                <span className="doc-num">22</span>
                                <span className="doc-name">Liquidity</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                        </StageDocChips>
                    </StageCard>

                    {/* Stage 3 - Market Context */}
                    <StageCard $borderAccent="#38bdf8">
                        <StageHeader>
                            <StageNumber $bg="rgba(56, 189, 248, 0.15)" $color="#38bdf8">03</StageNumber>
                            <StageTitleWrapper>
                                <StageTitle>Market Context</StageTitle>
                                <StageTagline>Objective Observation, Multi-TF Architecture & Cycles</StageTagline>
                            </StageTitleWrapper>
                        </StageHeader>
                        <StageRoleDesc>
                            The chart translator: establishes the fractal ladder (Step Rule), decodes the chronological AMD price cycle, objectively evaluates price behavior, distinguishes structure (Primary vs Candidate), and isolates trigger mechanics.
                        </StageRoleDesc>
                        <StageDocChips>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("timeframes")} title="Open Timeframes">
                                <span className="doc-num">29</span>
                                <span className="doc-name">Timeframes</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("market_cycles")} title="Open Market Cycles">
                                <span className="doc-num">25</span>
                                <span className="doc-name">Market Cycles</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("price_quality")} title="Open Price Quality">
                                <span className="doc-num">16</span>
                                <span className="doc-name">Price Quality</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("market_structure")} title="Open Market Structure">
                                <span className="doc-num">01</span>
                                <span className="doc-name">Market Structure</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("market_structure_2")} title="Open Market Structure - 2">
                                <span className="doc-num">15</span>
                                <span className="doc-name">Market Structure - 2</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("patterns_candles_chart")} title="Open Patterns">
                                <span className="doc-num">24</span>
                                <span className="doc-name">Patterns</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                        </StageDocChips>
                    </StageCard>

                    {/* Stage 4 - Key Price Zones */}
                    <StageCard $borderAccent="#10b981">
                        <StageHeader>
                            <StageNumber $bg="rgba(16, 185, 129, 0.15)" $color="#10b981">04</StageNumber>
                            <StageTitleWrapper>
                                <StageTitle>Key Price Zones</StageTitle>
                                <StageTagline>Institutional Cartography & Level Quality Filtering</StageTagline>
                            </StageTitleWrapper>
                        </StageHeader>
                        <StageRoleDesc>
                            Pinpoint high-probability reaction zones. Combines cardinal horizontal references (Key Levels), institutional order footprints (Supply / Demand), and strict anti-noise criteria (Levels Quality).
                        </StageRoleDesc>
                        <StageDocChips>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("key_levels")} title="Open Key Levels">
                                <span className="doc-num">06</span>
                                <span className="doc-name">Key Levels</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("supply_demand_zone")} title="Open Supply / Demand Zone">
                                <span className="doc-num">23</span>
                                <span className="doc-name">Supply / Demand Zone</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("levels_quality")} title="Open Levels Quality">
                                <span className="doc-num">05</span>
                                <span className="doc-name">Levels Quality</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                        </StageDocChips>
                    </StageCard>

                    {/* Stage 5 - Market Confluence */}
                    <StageCard $borderAccent="#818cf8">
                        <StageHeader>
                            <StageNumber $bg="rgba(129, 140, 248, 0.15)" $color="#818cf8">05</StageNumber>
                            <StageTitleWrapper>
                                <StageTitle>Market Confluence</StageTitle>
                                <StageTagline>Quantitative Dashboard & Contextual Filters</StageTagline>
                            </StageTitleWrapper>
                        </StageHeader>
                        <StageRoleDesc>
                            Mathematical and statistical validation. Calibrates normal swing sizes vs abnormal institutional expansion (Volatility Grid), verifies broad-market flow participation (Market Internals), and integrates dynamic institutional references (EMAs).
                        </StageRoleDesc>
                        <StageDocChips>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("volatility_grid")} title="Open Volatility Grid">
                                <span className="doc-num">27</span>
                                <span className="doc-name">Volatility Grid</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("contextual_filters")} title="Open Market Internals">
                                <span className="doc-num">17</span>
                                <span className="doc-name">Market Internals</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                            <BlueprintDocChip onClick={() => handleSelectDocFromHome("price_derived_metrics")} title="Open Price-Derived Metrics">
                                <span className="doc-num">26</span>
                                <span className="doc-name">Price-Derived Metrics</span>
                                <FiArrowRight size={12} className="doc-arrow" />
                            </BlueprintDocChip>
                        </StageDocChips>
                    </StageCard>
                </BlueprintStagesWrapper>

                {/* 03. Live Chart Execution Flow */}
                <WorkflowContainer>
                    <WorkflowHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FiCrosshair size={16} color="#80cbc4" />
                            <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>Chronological Live Chart Execution Roadmap</span>
                        </div>
                        <span style={{ fontSize: '0.74rem', opacity: 0.65 }}>From Macro HTF framing down to trade exit</span>
                    </WorkflowHeader>
                    <WorkflowStepsGrid>
                        <WorkflowStepBox onClick={() => handleSelectDocFromHome("timeframes")} title="Step 1: Timeframes">
                            <WorkflowStepNumber>1</WorkflowStepNumber>
                            <WorkflowStepText>Multi-TF Framing</WorkflowStepText>
                            <WorkflowStepSub>Step Rule & Pacing</WorkflowStepSub>
                        </WorkflowStepBox>
                        <WorkflowStepBox onClick={() => handleSelectDocFromHome("market_cycles")} title="Step 2: Market Cycles">
                            <WorkflowStepNumber>2</WorkflowStepNumber>
                            <WorkflowStepText>Market Cycle</WorkflowStepText>
                            <WorkflowStepSub>AMD Phase & Stop Run</WorkflowStepSub>
                        </WorkflowStepBox>
                        <WorkflowStepBox onClick={() => handleSelectDocFromHome("market_structure")} title="Step 3: Market Structure">
                            <WorkflowStepNumber>3</WorkflowStepNumber>
                            <WorkflowStepText>Structure & Pivots</WorkflowStepText>
                            <WorkflowStepSub>Primary HH/HL & Low</WorkflowStepSub>
                        </WorkflowStepBox>
                        <WorkflowStepBox onClick={() => handleSelectDocFromHome("key_levels")} title="Step 4: Key Levels & Zones">
                            <WorkflowStepNumber>4</WorkflowStepNumber>
                            <WorkflowStepText>Zones & Levels</WorkflowStepText>
                            <WorkflowStepSub>S/D + Levels Quality</WorkflowStepSub>
                        </WorkflowStepBox>
                        <WorkflowStepBox onClick={() => handleSelectDocFromHome("volatility_grid")} title="Step 5: Volatility Grid">
                            <WorkflowStepNumber>5</WorkflowStepNumber>
                            <WorkflowStepText>Quantitative Check</WorkflowStepText>
                            <WorkflowStepSub>Volatility & Internals</WorkflowStepSub>
                        </WorkflowStepBox>
                        <WorkflowStepBox onClick={() => handleSelectDocFromHome("patterns_candles_chart")} title="Step 6: Patterns">
                            <WorkflowStepNumber>6</WorkflowStepNumber>
                            <WorkflowStepText>Trigger & Rejection</WorkflowStepText>
                            <WorkflowStepSub>Pattern & Price Quality</WorkflowStepSub>
                        </WorkflowStepBox>
                        <WorkflowStepBox onClick={() => handleSelectDocFromHome("trade_plan")} title="Step 7: Trade Plan">
                            <WorkflowStepNumber>7</WorkflowStepNumber>
                            <WorkflowStepText>Trade Plan</WorkflowStepText>
                            <WorkflowStepSub>R:R Sizing & Invalidation</WorkflowStepSub>
                        </WorkflowStepBox>
                        <WorkflowStepBox onClick={() => handleSelectDocFromHome("trade_management")} title="Step 8: Trade Management">
                            <WorkflowStepNumber>8</WorkflowStepNumber>
                            <WorkflowStepText>Trade Management</WorkflowStepText>
                            <WorkflowStepSub>Scale-Out & Trailing</WorkflowStepSub>
                        </WorkflowStepBox>
                    </WorkflowStepsGrid>
                </WorkflowContainer>
            </BlueprintContainer>
        );
    };

    return (
        <DictionaryNavContext.Provider value={{ navigateToDoc, navigateToTag, terms }}>
            <Overlay onClick={onClose}>
                <Container onClick={e => e.stopPropagation()}>
                {/* Header */}
                <Header>
                    <HeaderTitleBlock>
                        {/* Switcher Navigation Arrows: < and > (to the left of dictionary icon) */}
                        <NavArrowsGroup>
                            <NavArrowBtn
                                onClick={handleGoBack}
                                disabled={!canGoBack}
                                title={viewMode === "home" ? "Return to document view (<)" : "Previous document (<)"}
                            >
                                <FiChevronLeft size={16} />
                            </NavArrowBtn>
                            <NavArrowBtn
                                onClick={handleGoForward}
                                disabled={!canGoForward}
                                title={viewMode === "home" ? "Return to document view (>)" : "Next document (>)"}
                            >
                                <FiChevronRight size={16} />
                            </NavArrowBtn>
                        </NavArrowsGroup>

                        <IconBadge
                            onClick={handleGoHome}
                            title="Dictionary Home (Catalog)"
                            role="button"
                            tabIndex={0}
                        >
                            <FiBookOpen size={22} />
                        </IconBadge>

                        <Title
                            onClick={handleGoHome}
                            title="Dictionary Home (Catalog)"
                        >
                            Dictionary
                        </Title>
                    </HeaderTitleBlock>

                    <HeaderActions>
                        <CloseBtn onClick={onClose} title="Close (Esc)">
                            <FiX size={18} />
                        </CloseBtn>
                    </HeaderActions>
                </Header>

                {/* Toolbar */}
                <Toolbar ref={searchContainerRef}>
                    <SearchBox>
                        <SearchIconWrapper>
                            <FiSearch size={16} />
                        </SearchIconWrapper>
                        <SearchInput
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search in doc content, rules, descriptions, diagrams, or keywords..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onFocus={() => {
                                if (searchQuery.trim().length > 0) {
                                    setIsSearchDropdownOpen(true);
                                }
                            }}
                            onKeyDown={handleSearchKeyDown}
                        />
                        {searchQuery && (
                            <ClearSearchBtn
                                onClick={() => {
                                    setSearchQuery("");
                                    setIsSearchDropdownOpen(false);
                                    if (searchInputRef.current) searchInputRef.current.focus();
                                }}
                                title="Clear search"
                            >
                                <FiX size={15} />
                            </ClearSearchBtn>
                        )}

                        {/* Search Proposals Dropdown */}
                        {isSearchDropdownOpen && searchQuery.trim() && (
                            <SuggestionsDropdown>
                                <SuggestionsHeader>
                                    <span>
                                        Proposed Docs ({filteredTerms.length} found)
                                    </span>
                                    <span style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'none', fontWeight: 500 }}>
                                        Use ↑↓ to navigate, Enter to view
                                    </span>
                                </SuggestionsHeader>

                                {filteredTerms.length > 0 ? (
                                    filteredTerms.map((term, idx) => {
                                        const isHighlighted = idx === highlightedIndex;
                                        return (
                                            <SuggestionItem
                                                key={term.id}
                                                active={isHighlighted}
                                                onMouseEnter={() => setHighlightedIndex(idx)}
                                                onClick={() => handleSelectProposal(term.id)}
                                            >
                                                <SuggestionTitleRow>
                                                    <SuggestionTitle>
                                                        <FiBookOpen size={14} color="#80cbc4" />
                                                        <HighlightText text={term.term} query={searchQuery} />
                                                    </SuggestionTitle>
                                                    <CategoryBadge style={{ fontSize: '0.68rem', padding: '2px 7px' }}>
                                                        {term.category}
                                                    </CategoryBadge>
                                                </SuggestionTitleRow>

                                                {term._snippet && (
                                                    <SuggestionSnippet>
                                                        <SnippetBadge>{term._snippet.label}</SnippetBadge>
                                                        <div>
                                                            <HighlightText text={term._snippet.snippet} query={searchQuery} />
                                                        </div>
                                                    </SuggestionSnippet>
                                                )}
                                            </SuggestionItem>
                                        );
                                    })
                                ) : (
                                    <div style={{ padding: '22px 16px', textAlign: 'center', opacity: 0.65, fontSize: '0.85rem' }}>
                                        No documentation matches "{searchQuery}".
                                    </div>
                                )}
                            </SuggestionsDropdown>
                        )}
                    </SearchBox>
                </Toolbar>

                {/* Content: Home View vs Detail View */}
                {viewMode === "home" ? (
                    <HomeContainer ref={homeContainerRef}>
                        <HomeHero>
                            <HomeHeroTitle>
                                <span>Categories</span>
                                <DocsCountPill>{groupedCategories.length} categories • {filteredTerms.length} docs</DocsCountPill>
                            </HomeHeroTitle>
                            <HomeHeroActions>
                                <HomeBlueprintBtn
                                    $active={showRoadmapGuide}
                                    onClick={() => setShowRoadmapGuide(prev => !prev)}
                                    title={showRoadmapGuide ? "Collapse System Blueprint" : "System Architecture & Roadmap Blueprint"}
                                    aria-label="System Architecture & Roadmap Blueprint"
                                >
                                    <FiCompass size={16} />
                                </HomeBlueprintBtn>

                                <DownloadAllPdfBtn
                                    onClick={handleExportAllPdf}
                                    disabled={isExportingAllPdf}
                                    title="Download full documentation (PDF)"
                                    aria-label="Download full documentation as PDF"
                                >
                                    <FiDownload size={15} className={isExportingAllPdf ? "pdf-spin" : ""} />
                                </DownloadAllPdfBtn>

                                {groupedCategories.length > 0 && (
                                    <HomeExpandAllBtn
                                        onClick={toggleAllHomeCategories}
                                        title={allHomeExpanded ? "Collapse All" : "Expand All"}
                                        aria-label={allHomeExpanded ? "Collapse All" : "Expand All"}
                                    >
                                        {allHomeExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                    </HomeExpandAllBtn>
                                )}
                            </HomeHeroActions>
                        </HomeHero>

                        {showRoadmapGuide && renderSystemBlueprint()}

                        {groupedCategories.length > 0 ? (
                            <HomeCategoriesList>
                                {groupedCategories.map(group => {
                                    const isExpanded = isHomeCategoryExpanded(group.name);
                                    const previewNames = group.terms.map(t => t.term).join(" • ");

                                    return (
                                        <HomeCategorySection key={group.name} $isExpanded={isExpanded}>
                                            <HomeCategoryHeader
                                                $isExpanded={isExpanded}
                                                onClick={() => toggleHomeCategory(group.name)}
                                                title={`Click to ${isExpanded ? 'collapse' : 'view'} ${group.name} documents`}
                                            >
                                                <HomeCategoryHeaderLeft>
                                                    <HomeCategoryIconWrapper $isExpanded={isExpanded}>
                                                        <FiFolder size={17} />
                                                    </HomeCategoryIconWrapper>
                                                    <div>
                                                        <HomeCategoryTitle>
                                                            {group.name}
                                                        </HomeCategoryTitle>
                                                        <HomeCategoryPreviewText>
                                                            {previewNames}
                                                        </HomeCategoryPreviewText>
                                                    </div>
                                                </HomeCategoryHeaderLeft>

                                                <HomeCategoryHeaderRight>
                                                    <DocsCountPill style={{ fontSize: '0.72rem', padding: '2px 9px' }}>
                                                        {group.terms.length} {group.terms.length === 1 ? 'doc' : 'docs'}
                                                    </DocsCountPill>
                                                    <HomeCategoryChevron $isExpanded={isExpanded}>
                                                        <FiChevronDown size={18} />
                                                    </HomeCategoryChevron>
                                                </HomeCategoryHeaderRight>
                                            </HomeCategoryHeader>

                                            {isExpanded && (
                                                <HomeCategoryCardsWrapper>
                                                    <DocCardsGrid style={{ marginTop: '12px' }}>
                                                        {group.terms.map(doc => (
                                                            <DocCard
                                                                key={doc.id}
                                                                onClick={() => handleSelectDocFromHome(doc.id)}
                                                            >
                                                                <DocCardHeader>
                                                                    <CategoryBadge>{doc.category}</CategoryBadge>
                                                                </DocCardHeader>

                                                                <div>
                                                                    <DocCardTitle>
                                                                        <HighlightText text={doc.term} query={searchQuery} />
                                                                    </DocCardTitle>
                                                                    <DocCardDesc style={{ marginTop: '8px' }}>
                                                                        <HighlightText text={(doc.shortDef || '').replace(/\s*---[\s\S]*$/, '').replace(/\s*###[\s\S]*$/, '').trim()} query={searchQuery} />
                                                                    </DocCardDesc>
                                                                </div>

                                                                <DocCardFooter>
                                                                    {doc.tags && doc.tags.length > 0 ? (
                                                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                                            {doc.tags.slice(0, 3).map(tag => (
                                                                                <TagChip key={tag} style={{ fontSize: '0.68rem', padding: '1px 6px' }}>
                                                                                    {tag}
                                                                                </TagChip>
                                                                            ))}
                                                                        </div>
                                                                    ) : <div />}

                                                                    <ViewDocPrompt>
                                                                        <span>Open</span>
                                                                        <FiArrowRight size={14} />
                                                                    </ViewDocPrompt>
                                                                </DocCardFooter>
                                                            </DocCard>
                                                        ))}
                                                    </DocCardsGrid>
                                                </HomeCategoryCardsWrapper>
                                            )}
                                        </HomeCategorySection>
                                    );
                                })}
                            </HomeCategoriesList>
                        ) : (
                            <div style={{ padding: '60px 20px', textAlign: 'center', opacity: 0.6, fontSize: '0.9rem' }}>
                                No documentation concepts match "{searchQuery}".
                            </div>
                        )}
                    </HomeContainer>
                ) : (
                    <BodyLayout>
                    {/* Dynamic Inverting Collapse/Expand Button (< -> >) */}
                    <CollapseToggleBtn
                        $isOpen={isSidebarOpen}
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        title={isSidebarOpen ? "Hide concepts sidebar" : "Show concepts sidebar"}
                    >
                        {isSidebarOpen ? <FiChevronLeft size={16} /> : <FiChevronRight size={16} />}
                    </CollapseToggleBtn>

                    {/* Left Sidebar: Terms list */}
                    <Sidebar $isOpen={isSidebarOpen}>
                        {/* Top Scroll Button */}
                        <ScrollNavBtn
                            $direction="up"
                            $isInactive={!canScrollUp}
                            onClick={handleScrollUp}
                            title="Scroll up concepts"
                        >
                            <FiChevronUp size={18} />
                        </ScrollNavBtn>

                        <TermsList ref={termsListRef}>
                            {groupedCategories.length > 0 ? (
                                groupedCategories.map(group => {
                                    const isExpanded = isCategoryExpanded(group.name);
                                    const hasSelectedTerm = group.terms.some(t => t.id === selectedTermId);

                                    return (
                                        <CategoryGroup key={group.name}>
                                            <CategoryHeader
                                                $isExpanded={isExpanded}
                                                $hasSelected={hasSelectedTerm}
                                                onClick={() => toggleCategory(group.name)}
                                                title={`Click to ${isExpanded ? 'collapse' : 'expand'} ${group.name}`}
                                            >
                                                <CategoryHeaderLeft>
                                                    <CategoryChevron $isExpanded={isExpanded}>
                                                        <FiChevronRight size={14} />
                                                    </CategoryChevron>
                                                    <CategoryTitle $isExpanded={isExpanded} $hasSelected={hasSelectedTerm}>
                                                        {group.name}
                                                    </CategoryTitle>
                                                </CategoryHeaderLeft>
                                                <CategoryCountBadge $hasSelected={hasSelectedTerm}>
                                                    {group.terms.length}
                                                </CategoryCountBadge>
                                            </CategoryHeader>

                                            {isExpanded && (
                                                <CategoryTermsContainer>
                                                    {group.terms.map(term => {
                                                        const isSelected = term.id === selectedTermId;
                                                        return (
                                                            <TermItem
                                                                key={term.id}
                                                                selected={isSelected}
                                                                onClick={() => {
                                                                    navigateToDoc(term.id);
                                                                }}
                                                            >
                                                                <TermBullet $selected={isSelected} />
                                                                <TermItemTitle selected={isSelected}>
                                                                    <HighlightText text={term.term} query={searchQuery} />
                                                                </TermItemTitle>
                                                            </TermItem>
                                                        );
                                                    })}
                                                </CategoryTermsContainer>
                                            )}
                                        </CategoryGroup>
                                    );
                                })
                            ) : (
                                <div style={{ padding: '30px 20px', textAlign: 'center', opacity: 0.6, fontSize: '0.85rem' }}>
                                    No definitions found matching "{searchQuery}".
                                </div>
                            )}
                        </TermsList>

                        {/* Bottom Scroll Button */}
                        <ScrollNavBtn
                            $direction="down"
                            $isInactive={!canScrollDown}
                            onClick={handleScrollDown}
                            title="Scroll down concepts"
                        >
                            <FiChevronDown size={18} />
                        </ScrollNavBtn>
                    </Sidebar>

                    {/* Right Panel: Active Term Detail */}
                    {activeTerm ? (
                        <DetailPanel
                            key={selectedTermId}
                            ref={detailPanelRef}
                            $isSidebarOpen={isSidebarOpen}
                            onScroll={(e) => {
                                if (selectedTermId) {
                                    docScrollPositionsRef.current[selectedTermId] = e.currentTarget.scrollTop;
                                }
                            }}
                        >
                            {isEditing && editingTerm ? (
                                <InlineEditForm onSubmit={handleSaveTerm}>
                                    <DetailHeader style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
                                        <div>
                                            <DetailTitle>
                                                Edit: {editingTerm.term || "Concept"}
                                            </DetailTitle>
                                            <DetailMetaRow>
                                                <CategoryBadge>{editingTerm.category}</CategoryBadge>
                                                <span style={{ fontSize: '0.78rem', opacity: 0.6 }}>Direct In-Page Editing</span>
                                            </DetailMetaRow>
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <ActionBtn
                                                iconOnly
                                                primary
                                                type="submit"
                                                onClick={handleSaveTerm}
                                                title="Save changes"
                                            >
                                                <FiCheck size={16} />
                                            </ActionBtn>
                                            <ActionBtn
                                                iconOnly
                                                type="button"
                                                onClick={handleCancelEdit}
                                                title="Cancel editing"
                                            >
                                                <FiX size={16} />
                                            </ActionBtn>
                                        </div>
                                    </DetailHeader>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <FormGroup>
                                            <Label>Term / Concept Name *</Label>
                                            <Input
                                                type="text"
                                                required
                                                placeholder="e.g. Breaker Block, SMT Divergence, Primary Low..."
                                                value={editingTerm.term}
                                                onChange={e => setEditingTerm({ ...editingTerm, term: e.target.value })}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <Label>Category</Label>
                                            <Select
                                                value={editingTerm.category}
                                                onChange={e => setEditingTerm({ ...editingTerm, category: e.target.value })}
                                            >
                                                {DEFAULT_DICTIONARY_CATEGORIES.map(c => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </Select>
                                        </FormGroup>
                                    </div>

                                    <FormGroup>
                                        <Label>Short Definition (1-2 sentences)</Label>
                                        <Textarea
                                            rows={2}
                                            placeholder="Concise summary of the concept..."
                                            value={editingTerm.shortDef || ""}
                                            onChange={e => setEditingTerm({ ...editingTerm, shortDef: e.target.value })}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Anchor Rule or Formula (Optional)</Label>
                                        <Input
                                            type="text"
                                            placeholder="e.g. Line(PL1, PL2) or Candle Close > Pivot"
                                            value={editingTerm.ruleOrFormula || ""}
                                            onChange={e => setEditingTerm({ ...editingTerm, ruleOrFormula: e.target.value })}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Chart Image / Screenshot (URL or File)</Label>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                                            <Input
                                                type="text"
                                                placeholder="e.g. /docs/images/my-chart.svg or URL https://..."
                                                value={editingTerm.imageUrl || ""}
                                                onChange={e => setEditingTerm({ ...editingTerm, imageUrl: e.target.value })}
                                                style={{ flex: 1 }}
                                            />
                                            <label
                                                title="Upload Image"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '6px',
                                                    border: '1px solid rgba(76, 206, 172, 0.4)',
                                                    background: 'rgba(76, 206, 172, 0.1)',
                                                    color: '#4cceac',
                                                    cursor: 'pointer',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <FiUpload size={16} />
                                                <input
                                                    type="file"
                                                    accept="image/*,.svg"
                                                    style={{ display: 'none' }}
                                                    onChange={handleImageUpload}
                                                />
                                            </label>
                                            {editingTerm.imageUrl && (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingTerm({ ...editingTerm, imageUrl: "", imageCaption: "" })}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '36px',
                                                        height: '36px',
                                                        borderRadius: '6px',
                                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                                        background: 'rgba(239, 68, 68, 0.1)',
                                                        color: '#ef4444',
                                                        cursor: 'pointer',
                                                        flexShrink: 0
                                                    }}
                                                    title="Remove Image"
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                        <Input
                                            type="text"
                                            placeholder="Image Caption (e.g. EURUSD 15M - Market Structure Logic)"
                                            value={editingTerm.imageCaption || ""}
                                            onChange={e => setEditingTerm({ ...editingTerm, imageCaption: e.target.value })}
                                            style={{ marginBottom: '8px' }}
                                        />
                                        {editingTerm.imageUrl && (
                                            <div style={{
                                                position: 'relative',
                                                maxHeight: '180px',
                                                overflow: 'hidden',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                background: '#090d18',
                                                textAlign: 'center',
                                                padding: '6px'
                                            }}>
                                                <img src={editingTerm.imageUrl} alt="Preview" style={{ maxHeight: '160px', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                                            </div>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>ASCII Diagram / Schema (Optional)</Label>
                                        <TextareaMonospace
                                            rows={8}
                                            placeholder="Paste or draw your ASCII structural schema here..."
                                            value={editingTerm.diagram || ""}
                                            onChange={e => setEditingTerm({ ...editingTerm, diagram: e.target.value })}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Detailed Rules & Mechanics</Label>
                                        <Textarea
                                            rows={6}
                                            placeholder="Validation rules, common traps to avoid, institutional mechanics..."
                                            value={editingTerm.fullDescription || ""}
                                            onChange={e => setEditingTerm({ ...editingTerm, fullDescription: e.target.value })}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Strategic Application & Trade Setup (Multi-line & Lists supported)</Label>
                                        <Textarea
                                            rows={4}
                                            placeholder="e.g.&#10;1. **Execution**: ...&#10;2. **Confirmation**: ...&#10;3. **Invalidation**: ..."
                                            value={editingTerm.application || ""}
                                            onChange={e => setEditingTerm({ ...editingTerm, application: e.target.value })}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Tags (comma-separated)</Label>
                                        <Input
                                            type="text"
                                            placeholder="Structure, Pivots, Liquidity, Entry..."
                                            value={Array.isArray(editingTerm.tags) ? editingTerm.tags.join(', ') : editingTerm.tags || ""}
                                            onChange={e => setEditingTerm({ ...editingTerm, tags: e.target.value })}
                                        />
                                    </FormGroup>

                                    {/* Bottom Action Buttons: Save & Cancel */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginTop: '10px',
                                        paddingTop: '16px',
                                        paddingBottom: '8px',
                                        borderTop: '1px solid rgba(255, 255, 255, 0.08)'
                                    }}>
                                        <ActionBtn
                                            iconOnly
                                            primary
                                            type="submit"
                                            onClick={handleSaveTerm}
                                            title="Save changes"
                                        >
                                            <FiCheck size={16} />
                                        </ActionBtn>
                                        <ActionBtn
                                            iconOnly
                                            type="button"
                                            onClick={handleCancelEdit}
                                            title="Cancel editing"
                                        >
                                            <FiX size={16} />
                                        </ActionBtn>
                                    </div>
                                </InlineEditForm>
                            ) : (
                                <>
                                    <DetailHeader>
                                        <div>
                                            <DetailTitle>
                                                <HighlightText text={activeTerm.term} query={searchQuery} />
                                            </DetailTitle>
                                            <DetailMetaRow>
                                                <CategoryBadge>{activeTerm.category}</CategoryBadge>
                                                {activeTerm.isCustom && <CustomBadge>Custom</CustomBadge>}
                                            </DetailMetaRow>
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <ActionBtn
                                                iconOnly
                                                onClick={() => handleExportPdf(activeTerm)}
                                                title="Export document as PDF"
                                            >
                                                <FiDownload size={16} />
                                            </ActionBtn>

                                            <ActionBtn
                                                iconOnly
                                                onClick={() => handleCopyTerm(activeTerm)}
                                                title={copiedId === activeTerm.id ? "Copied to clipboard!" : "Copy full reference card"}
                                            >
                                                {copiedId === activeTerm.id ? (
                                                    <FiCheck size={16} color="#4cceac" />
                                                ) : (
                                                    <FiCopy size={16} />
                                                )}
                                            </ActionBtn>

                                            <ActionBtn
                                                iconOnly
                                                onClick={() => handleOpenEdit(activeTerm)}
                                                title="Edit this definition"
                                            >
                                                <FiEdit2 size={16} />
                                            </ActionBtn>

                                            {activeTerm.isCustom && (
                                                <ActionBtn
                                                    iconOnly
                                                    danger
                                                    onClick={() => handleDeleteTerm(activeTerm.id)}
                                                    title="Delete"
                                                >
                                                    <FiTrash2 size={16} />
                                                </ActionBtn>
                                            )}
                                        </div>
                                    </DetailHeader>

                                    {/* Short Definition */}
                                    {activeTerm.shortDef && (() => {
                                        const cleanShortDef = activeTerm.shortDef
                                            .replace(/\s*---[\s\S]*$/, "")
                                            .replace(/\s*###[\s\S]*$/, "")
                                            .replace(/\s*##[\s\S]*$/, "")
                                            .trim();
                                        if (!cleanShortDef) return null;
                                        return (
                                            <ShortDefCallout>
                                                {renderInlineMarkdown(cleanShortDef, searchQuery)}
                                            </ShortDefCallout>
                                        );
                                    })()}

                                    {/* Detailed Explanation */}
                                    {activeTerm.fullDescription && (
                                        <SectionBlock>
                                            <DocRichViewer
                                                content={activeTerm.fullDescription}
                                                query={searchQuery}
                                                onOpenLightbox={setLightboxImage}
                                                illustrationNode={shouldInsertIllustrationBeforeKeyLevels ? renderIllustrationBlock() : null}
                                                insertIllustrationBeforeTitle="Primary Structure and Key Price Levels"
                                            />
                                        </SectionBlock>
                                    )}

                                    {/* Diagram / ASCII Art */}
                                    {activeTerm.diagram && (
                                        <SectionBlock>
                                            <SectionHeading>
                                                <FiMaximize2 size={14} />
                                                Structural Diagram & Visual Rule
                                            </SectionHeading>
                                            <DiagramContainer>
                                                <CopyDiagramBtn
                                                    onClick={() => handleCopyDiagram(activeTerm.diagram)}
                                                    title={copiedId === "diagram" ? "Copied diagram!" : "Copy diagram"}
                                                >
                                                    {copiedId === "diagram" ? <FiCheck size={14} color="#4cceac" /> : <FiCopy size={14} />}
                                                </CopyDiagramBtn>
                                                <pre>{activeTerm.diagram}</pre>
                                            </DiagramContainer>
                                        </SectionBlock>
                                    )}

                                    {/* Visual Chart Image (rendered here only if not inserted inline above) */}
                                    {activeTerm.imageUrl && !shouldInsertIllustrationBeforeKeyLevels && (
                                        renderIllustrationBlock()
                                    )}

                                    {/* Strategic Application */}
                                    {activeTerm.application && (
                                        <SectionBlock>
                                            <DocRichViewer
                                                content={activeTerm.application}
                                                query={searchQuery}
                                                onOpenLightbox={setLightboxImage}
                                            />
                                        </SectionBlock>
                                    )}


                                    {/* Tags */}
                                    {activeTerm.tags && activeTerm.tags.length > 0 && (
                                        <SectionBlock>
                                            <TagsRow>
                                                {activeTerm.tags.map(tag => {
                                                    const resolvedTagDocId = resolveTagToTermId(tag, terms);
                                                    const isTagLink = Boolean(resolvedTagDocId);
                                                    return (
                                                        <TagChip
                                                            key={tag}
                                                            onClick={isTagLink ? () => navigateToDoc(resolvedTagDocId, { targetSection: 'tag-target', tagText: tag }) : undefined}
                                                            style={{
                                                                cursor: isTagLink ? 'pointer' : 'default',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            title={isTagLink ? `Open section for: ${tag}` : undefined}
                                                        >
                                                            <FiTag size={11} />
                                                            {tag}
                                                            {isTagLink && <span style={{ fontSize: '0.62rem', opacity: 0.7 }}>↗</span>}
                                                        </TagChip>
                                                    );
                                                })}
                                            </TagsRow>
                                        </SectionBlock>
                                    )}
                                </>
                            )}
                        </DetailPanel>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                            Select a definition to view its contents.
                        </div>
                    )}
                </BodyLayout>
                )}
            </Container>
            </Overlay>

            {/* Fullscreen Lightbox Zoom Modal */}
            {lightboxImage && (
                <LightboxOverlay
                    onClick={(e) => {
                        e.stopPropagation();
                        setLightboxImage(null);
                    }}
                >
                    <LightboxCloseBtn
                        onClick={(e) => {
                            e.stopPropagation();
                            setLightboxImage(null);
                        }}
                        title="Fermer le zoom"
                    >
                        <FiX size={20} />
                    </LightboxCloseBtn>

                    {activeImages.length > 1 && (
                        <>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrevImage();
                                }}
                                title="Image précédente (<)"
                                style={{
                                    position: 'fixed',
                                    left: '24px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'rgba(9, 13, 24, 0.85)',
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    color: '#ffffff',
                                    borderRadius: '50%',
                                    width: '46px',
                                    height: '46px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.7)',
                                    zIndex: 2600,
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <FiChevronLeft size={26} />
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNextImage();
                                }}
                                title="Image suivante (>)"
                                style={{
                                    position: 'fixed',
                                    right: '24px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'rgba(9, 13, 24, 0.85)',
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    color: '#ffffff',
                                    borderRadius: '50%',
                                    width: '46px',
                                    height: '46px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.7)',
                                    zIndex: 2600,
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <FiChevronRight size={26} />
                            </button>
                        </>
                    )}

                    <LightboxImg
                        src={lightboxImage}
                        alt={currentIllustration?.caption || "Zoomed Chart Diagram"}
                        onClick={e => e.stopPropagation()}
                    />

                    {currentIllustration && (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                marginTop: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                background: 'rgba(9, 13, 24, 0.85)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                padding: '6px 18px',
                                borderRadius: '20px',
                                color: '#e2e8f0',
                                fontSize: '0.85rem',
                                zIndex: 2600
                            }}
                        >
                            {activeImages.length > 1 && (
                                <span style={{ fontWeight: '700', color: '#38bdf8' }}>
                                    {currentIllustrationIndex + 1} / {activeImages.length}
                                </span>
                            )}
                            {currentIllustration.caption && (
                                <span style={{ color: '#cbd5e1' }}>{currentIllustration.caption}</span>
                            )}
                        </div>
                    )}
                </LightboxOverlay>
            )}
        </DictionaryNavContext.Provider>
    );
};

export default TechnicalDictionaryModal;

